class AAWorkspaceShell {
  constructor(root) {
  this.root = root;
  this.toggle = root.querySelector('[data-aa-menu-toggle]');
  this.overlay = root.querySelector('[data-aa-overlay]');
  this.sidebar = root.querySelector('[data-aa-sidebar]');

  this.previousPlaybooks = [];
  this.pastPlaybookVisibleCount = 6;
  this.clientAccount = null;
  this.clientConfiguration = null;

  this.catalogRecords = [];
  this.catalogLoadError = null;
  this.catalogYearFilter = '';
  this.catalogBrandFilter = '';
  this.clientCatalogSection = 'history';
  this.clientCoopPreview = null;

  this.marketingPlan = null;
  this.marketingPackage = null;
  this.packageChangeRequests = [];
  this.packageChangeOpen = false;
  this.packageChangeMessage = '';
  this.connectedAccounts = [];
  this.accessDetailsTarget = null;
  this.accessDetailsSubmission = null;
  this.contracts = null;
  this.invoices = [];
  this.invoiceLoadError = '';
  this.brandAssets = [];
  this.vendorBrands = [];

  this.init();
}

   async init() {
  

  this.bindEvents();
  this.bindCatalogFilters();
  this.bindConnectedAccountActions();
  this.bindPackageChangeActions();
  this.bindBrandAssetActions();

  

  const adminClientId =
    await window.AAAdminWorkspaceView
      ?.getAuthorizedClientId?.();

  // The admin client name has already been verified against Supabase.
  // Show it while the rest of the Workspace initializes.
  const verifiedClientName = adminClientId
    ? window.AAAdminWorkspaceView?.getClientName?.()
    : null;

  if (verifiedClientName) {
    this.root.querySelectorAll('[data-aa-client-name]').forEach((element) => {
      element.textContent = verifiedClientName;
      element.setAttribute('aria-busy', 'false');
    });
  }

  // The account identity was verified during the admin-client lookup.
  // Populate it before waiting for the current Playbook to initialize.
  const verifiedAccount = adminClientId
    ? window.AAAdminWorkspaceView?.getVerifiedAccountIdentity?.()
    : null;
  if (verifiedAccount) this.renderAccountIdentity(verifiedAccount);

  await window.AAWorkspaceContext.start({
    clientId: adminClientId || undefined
  });

  window.AAAdminWorkspaceView
    ?.enhanceWorkspace?.();

  

  const workspace = window.AAWorkspaceContext.get();
  

  if (!workspace?.user || workspace?.authLocked) {
    window.AAWorkspaceContext.redirectToLogin();
    return;
  }

  // Missing or temporarily unavailable client identity must not display
  // another client's data or unexpectedly navigate back to Headquarters.
  if (!workspace.clientId) {
    const content = this.root.querySelector('.aa-workspace__content');
    if (content) {
      content.innerHTML = `
        <section class="aa-client-catalog-state" role="alert">
          <h2>Workspace could not load</h2>
          <p>We couldn't confirm the selected client. No client data was loaded.</p>
          <button type="button" class="aa-button aa-button--primary" data-aa-workspace-retry>Try Again</button>
          <a class="aa-button aa-button--secondary" href="/pages/artisans-avenue-headquarters">Back to Headquarters</a>
        </section>
      `;
      content.querySelector('[data-aa-workspace-retry]')
        ?.addEventListener('click', () => window.location.reload());
    }
    return;
  }

  this.renderAccountIdentity();

  // Keep the validated header and sidebar mounted on ordinary page changes.
  window.AAWorkspaceRouter?.attach?.(this);

  // Client Owners can request employee access without leaving their workspace.
  // The server validates owner membership again before showing or saving anything.
  window.AAClientTeamOwner?.mount?.(workspace);

 

// A failing optional section should not delay or block other sections.
const loadSafe = (label, load) =>
  Promise.resolve()
    .then(load)
    .catch((error) => {
      console.error(`Unable to load ${label}:`, error);
    });

// Identify the client and load the navigation configuration independently.
const accountReady = loadSafe(
  'client account',
  () => this.loadClientAccount()
).then(() => this.renderClientName());

const modulesReady = loadSafe(
  'client modules',
  () => this.loadClientModules()
);

// Run only the required DOM-backed page modules. The methods themselves
// check whether their target section exists before requesting data.
await Promise.all([
  accountReady,

  modulesReady.then(() => Promise.all([
    loadSafe('email workspace', () => window.AAEmailWorkspace?.init?.({
      root: this.root,
      clientId: workspace.clientId,
      enabled: this.hasClientEmailModule()
    })),
    loadSafe('catalog history', () => this.loadCatalogHistory())
  ])),

  accountReady.then(() => loadSafe('dashboard', async () => {
    await this.loadDashboard();
    // These alerts share the dashboard action container and must be
    // appended after its base content has loaded.
    await this.loadDashboardAccountAlerts();
  })),

  loadSafe('dashboard upcoming items', () => this.loadDashboardComingUp()),
  loadSafe('marketing plan', () => this.loadMarketingPlan()),
  loadSafe('marketing package', () => this.loadMarketingPackage()),
  loadSafe('connected accounts', () => this.loadConnectedAccounts()),
  loadSafe('contract center', () => this.loadContractCenter()),
  loadSafe('invoices', () => this.loadInvoiceCenter()),
  loadSafe('brand assets', () => this.loadBrandAssets()),
  accountReady.then(() => loadSafe('account details', () => this.loadAccount())),
  loadSafe('playbook library', () => this.loadPreviousPlaybooks())
]);
}

async loadClientAccount() {
  const workspace = window.AAWorkspaceContext.get();

  if (!workspace?.clientId || !window.AAPlaybookService) return null;

  this.clientAccount = await window.AAPlaybookService.getClientAccount({
    clientId: workspace.clientId
  });

  const clientName =
    this.clientAccount?.business_name ||
    this.clientAccount?.client_name ||
    (workspace?.isInternal
      ? window.AAAdminWorkspaceView?.getClientName?.()
      : null) ||
    null;

  window.AA_WORKSPACE.clientName = clientName;

  return this.clientAccount;
}

renderClientName() {
  const workspace = window.AAWorkspaceContext.get();
  const clientName = workspace?.clientName ||
    (workspace?.isInternal
      ? window.AAAdminWorkspaceView?.getClientName?.()
      : null) ||
    'Client unavailable';

  this.root.querySelectorAll('[data-aa-client-name]').forEach((el) => {
    el.textContent = clientName;
    el.setAttribute('aria-busy', 'false');
  });
}

escapeCatalogHtml(value = '') {
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

hasClientEmailModule() {
  const access = Array.isArray(this.clientConfiguration?.access)
    ? this.clientConfiguration.access
    : [];

  return access.some(
    (item) =>
      String(item).trim().toLowerCase() === 'email'
  );
}

hasClientCatalogService() {
  const services = Array.isArray(this.clientConfiguration?.services)
    ? this.clientConfiguration.services
    : [];

  return services.some((service) =>
    String(service)
      .trim()
      .toLowerCase()
      .includes('catalog')
  );
}

hasClientCatalogModule() {
  return (
    this.hasClientCatalogService() ||
    this.catalogRecords.length > 0
  );
}

async loadClientModules() {
  const workspace = window.AAWorkspaceContext.get();

  if (!workspace?.clientId) return;

  // Configuration and catalog history are independent, scoped queries.
  // Fetch both together so the navigation needn't wait on two round trips.
  await Promise.all([
    (async () => {
      try {
        this.clientConfiguration =
          await window.AASupabaseClient?.getClientConfiguration?.({
            clientId: workspace.clientId
          }) || null;
      } catch (error) {
        console.error('Unable to load client module configuration:', error);
        this.clientConfiguration = null;
      }
    })(),

    (async () => {
      try {
        if (!window.AASupabaseClient?.getCatalogRecords) {
          throw new Error('Catalog history service is unavailable.');
        }

        // Catalogs can only be loaded for the resolved Workspace client.
        this.catalogRecords =
          await window.AASupabaseClient.getCatalogRecords({
            clientId: workspace.clientId
          });

        this.catalogLoadError = null;
      } catch (error) {
        console.error('Unable to load client catalog history:', error);
        this.catalogRecords = [];
        this.catalogLoadError = error;
      }
    })()
  ]);

  this.renderClientModuleNavigation();
}

renderClientModuleNavigation() {
  const nav =
    this.root.querySelector('[data-aa-client-module-nav]');

  if (!nav) {
    return;
  }

  const currentPath =
    window.location.pathname.replace(/\/+$/, '');

  const workspace =
    window.AAWorkspaceContext.get();

  const adminClientId =
    workspace?.isInternal
      ? window.AAAdminWorkspaceView?.getClientId?.()
      : null;

  const adminQuery =
    adminClientId
      ? `?aa_admin_client=${encodeURIComponent(adminClientId)}`
      : '';

  const dashboardUrl =
    `/pages/workspace-dashboard${adminQuery}`;

  const catalogsUrl =
    `/pages/workspace-catalogs${adminQuery}`;

  const emailUrl =
    `/pages/workspace-email${adminQuery}`;

  const links = [];

  if (this.hasClientEmailModule()) {
    links.push(`
      <a
        href="${emailUrl}"
        class="aa-workspace__nav-link ${
          currentPath === '/pages/workspace-email'
            ? 'is-active'
            : ''
        }"
        data-aa-client-email-nav
      >
        <span>Email</span>
      </a>
    `);
  }


  if (this.hasClientCatalogModule()) {
    links.push(`
      <a
        href="${catalogsUrl}"
        class="aa-workspace__nav-link ${
          currentPath === '/pages/workspace-catalogs'
            ? 'is-active'
            : ''
        }"
        data-aa-client-catalog-nav
      >
       <span>Catalogs & Co-op</span>
      </a>
    `);
  }

  nav.innerHTML = links.join('');

  const emailModule =
    this.root.querySelector('[data-aa-email-module]');

  if (emailModule) {
    emailModule.hidden =
      !this.hasClientEmailModule();
  }

  if (
    currentPath === '/pages/workspace-catalogs' &&
    !this.hasClientCatalogModule() &&
    !this.catalogLoadError
  ) {
    window.location.replace(dashboardUrl);
    return;
  }

  if (
    currentPath === '/pages/workspace-email' &&
    !this.hasClientEmailModule()
  ) {
    window.location.replace(dashboardUrl);
  }
}

bindCatalogFilters() {
  this.root.addEventListener(
    'change',
    (event) => {
      const yearFilter =
        event.target.closest(
          '[data-aa-catalog-year]'
        );

      const brandFilter =
        event.target.closest(
          '[data-aa-catalog-brand]'
        );

      const coopPeriod =
        event.target.closest(
          '[data-aa-client-coop-period]'
        );

      const allManufacturers =
        event.target.closest(
          '[data-aa-client-coop-all-manufacturers]'
        );

      const manufacturerOption =
        event.target.closest(
          '[data-aa-client-coop-manufacturer]'
        );

      if (yearFilter) {
        this.catalogYearFilter =
          yearFilter.value;

        this.renderCatalogRecords();
        return;
      }

      if (brandFilter) {
        this.catalogBrandFilter =
          brandFilter.value;

        this.renderCatalogRecords();
        return;
      }

      if (coopPeriod) {
        const custom =
          this.root.querySelector(
            '[data-aa-client-coop-custom]'
          );

        if (custom) {
          custom.hidden =
            coopPeriod.value !==
            'custom';
        }

        return;
      }

      if (allManufacturers) {
        const checked =
          allManufacturers.checked;

        this.root
          .querySelectorAll(
            '[data-aa-client-coop-manufacturer]'
          )
          .forEach((input) => {
            input.checked = false;
            input.disabled = checked;
          });

        return;
      }

      if (manufacturerOption) {
        const all =
          this.root.querySelector(
            '[data-aa-client-coop-all-manufacturers]'
          );

        const selectedCount =
          this.root.querySelectorAll(
            '[data-aa-client-coop-manufacturer]:checked'
          ).length;

        if (all) {
          all.checked =
            selectedCount === 0;

          this.root
            .querySelectorAll(
              '[data-aa-client-coop-manufacturer]'
            )
            .forEach((input) => {
              input.disabled =
                all.checked;
            });
        }
      }
    }
  );

  this.root.addEventListener(
    'click',
    async (event) => {
      const openPdfButton =
        event.target.closest(
          '[data-aa-client-open-catalog-pdf]'
        );

      if (openPdfButton) {
        const path =
          openPdfButton.dataset
            .aaClientOpenCatalogPdf ||
          '';

        if (!path) {
          return;
        }

        openPdfButton.disabled = true;
        const originalText =
          openPdfButton.textContent;

        openPdfButton.textContent =
          'Opening...';

        try {
          const url =
            await window.AASupabaseClient
              .getCatalogPdfSignedUrl({
                path
              });

          window.open(
            url,
            '_blank',
            'noopener,noreferrer'
          );
        } catch (error) {
          console.error(
            'Unable to open catalog PDF:',
            error
          );
        } finally {
          if (
            openPdfButton.isConnected
          ) {
            openPdfButton.disabled =
              false;
            openPdfButton.textContent =
              originalText;
          }
        }

        return;
      }

      const closePreviewButton =
        event.target.closest(
          '[data-aa-client-close-coop-preview]'
        );

      if (closePreviewButton) {
        this.clientCoopPreview =
          null;

        const preview =
          this.root.querySelector(
            '[data-aa-client-coop-preview]'
          );

        if (preview) {
          preview.hidden = true;
          preview.innerHTML = '';
        }

        return;
      }

      const downloadButton =
        event.target.closest(
          '[data-aa-client-download-coop]'
        );

      if (
        downloadButton &&
        this.clientCoopPreview
      ) {
        const originalText =
          downloadButton.textContent;

        downloadButton.disabled =
          true;

        downloadButton.textContent =
          'Building PDF...';

        try {
          await this
            .downloadClientCoopReport(
              this.clientCoopPreview
            );
        } catch (error) {
          console.error(
            'Unable to generate Co-op PDF:',
            error
          );

          window.alert(
            'We could not generate the Co-op PDF. Please try again.'
          );
        } finally {
          if (
            downloadButton.isConnected
          ) {
            downloadButton.disabled =
              false;

            downloadButton.textContent =
              originalText;
          }
        }
      }
    }
  );

  this.root.addEventListener(
    'submit',
    (event) => {
      const form =
        event.target.closest(
          '[data-aa-client-coop-form]'
        );

      if (!form) {
        return;
      }

      event.preventDefault();

      this.generateClientCoopReport(
        form
      );
    }
  );
}

populateClientCoopFilters() {
  const periodSelect =
    this.root.querySelector(
      '[data-aa-client-coop-period]'
    );

  const manufacturerContainer =
    this.root.querySelector(
      '[data-aa-client-coop-manufacturers]'
    );

  const allManufacturers =
    this.root.querySelector(
      '[data-aa-client-coop-all-manufacturers]'
    );

  if (
    !periodSelect ||
    !manufacturerContainer
  ) {
    return;
  }

  const years = [
    ...new Set(
      this.catalogRecords
        .map(
          (record) =>
            this.getCatalogYear(
              record
            )
        )
        .filter(Boolean)
    )
  ].sort(
    (a, b) =>
      Number(b) - Number(a)
  );

  const brands = [
    ...new Set(
      this.catalogRecords.flatMap(
        (record) =>
          Array.isArray(
            record.brands
          )
            ? record.brands
                .map(
                  (brand) =>
                    String(brand)
                      .trim()
                )
                .filter(Boolean)
            : []
      )
    )
  ].sort((a, b) =>
    a.localeCompare(
      b,
      undefined,
      { sensitivity: 'base' }
    )
  );

  periodSelect.innerHTML = `
    ${years.map((year) => `
      <option value="${this.escapeCatalogHtml(year)}">
        ${this.escapeCatalogHtml(year)}
      </option>
    `).join('')}

    ${years.length
      ? ''
      : `
        <option
          value="${new Date().getFullYear()}"
        >
          ${new Date().getFullYear()}
        </option>
      `
    }

    <option value="custom">
      Custom range
    </option>
  `;

  manufacturerContainer.innerHTML =
    brands.map((brand) => `
      <label class="aa-client-coop-manufacturer-option">
        <input
          type="checkbox"
          value="${this.escapeCatalogHtml(brand)}"
          data-aa-client-coop-manufacturer
          disabled
        >
        <span>
          ${this.escapeCatalogHtml(brand)}
        </span>
      </label>
    `).join('');

  if (allManufacturers) {
    allManufacturers.checked = true;
  }
}

generateClientCoopReport(form) {
  const period =
    form.querySelector(
      '[data-aa-client-coop-period]'
    )?.value ||
    '';

  const allManufacturers =
    Boolean(
      form.querySelector(
        '[data-aa-client-coop-all-manufacturers]'
      )?.checked
    );

  const selectedManufacturers =
    [
      ...form.querySelectorAll(
        '[data-aa-client-coop-manufacturer]:checked'
      )
    ]
      .map(
        (input) =>
          String(input.value || '')
            .trim()
      )
      .filter(Boolean);

  let dateStart = '';
  let dateEnd = '';
  let periodLabel = '';

  if (period === 'custom') {
    const startMonth =
      form.querySelector(
        '[data-aa-client-coop-start]'
      )?.value ||
      '';

    const endMonth =
      form.querySelector(
        '[data-aa-client-coop-end]'
      )?.value ||
      '';

    if (
      !startMonth ||
      !endMonth ||
      startMonth > endMonth
    ) {
      return;
    }

    dateStart =
      `${startMonth}-01`;

    dateEnd =
      `${endMonth}-01`;

    periodLabel =
      `${this.formatCatalogMonth(dateStart)} – ${this.formatCatalogMonth(dateEnd)}`;
  } else {
    const year =
      Number(period);

    if (!Number.isInteger(year)) {
      return;
    }

    dateStart =
      `${year}-01-01`;

    dateEnd =
      `${year}-12-01`;

    periodLabel =
      String(year);
  }

  const manufacturerKeys =
    new Set(
      selectedManufacturers.map(
        (manufacturer) =>
          manufacturer.toLowerCase()
      )
    );

  const catalogs =
    this.catalogRecords
      .filter((catalog) => {
        const month =
          String(
            catalog.catalog_month ||
            ''
          ).slice(0, 10);

        const dateMatches =
          month >= dateStart &&
          month <= dateEnd;

        const brandMatches =
          allManufacturers ||
          (
            Array.isArray(
              catalog.brands
            ) &&
            catalog.brands.some(
              (brand) =>
                manufacturerKeys.has(
                  String(brand)
                    .trim()
                    .toLowerCase()
                )
            )
          );

        return (
          dateMatches &&
          brandMatches
        );
      })
      .sort((a, b) =>
        String(
          a.catalog_month ||
          ''
        ).localeCompare(
          String(
            b.catalog_month ||
            ''
          )
        )
      );

  if (!catalogs.length) {
    return;
  }

  const manufacturerLabel =
    allManufacturers
      ? ''
      : selectedManufacturers
          .join(', ');

  this.clientCoopPreview = {
    reportTitle:
      `${periodLabel} Co-op Marketing Catalog Summary`,
    periodLabel,
    manufacturer:
      manufacturerLabel,
    catalogs
  };

  const preview =
    this.root.querySelector(
      '[data-aa-client-coop-preview]'
    );

  if (!preview) {
    return;
  }

  preview.hidden = false;
  preview.innerHTML = `
    <div class="aa-client-coop-preview__header">
      <div>
        <p class="aa-workspace__eyebrow">
          Report Preview
        </p>
        <h3>
          ${this.escapeCatalogHtml(
            this.clientCoopPreview
              .reportTitle
          )}
        </h3>
      </div>

      <div class="aa-client-coop-preview__actions">
        <button
          type="button"
          class="aa-button aa-button--secondary"
          data-aa-client-close-coop-preview
        >
          Close Preview
        </button>

        <button
          type="button"
          class="aa-button aa-button--primary"
          data-aa-client-download-coop
        >
          Download PDF
        </button>
      </div>
    </div>

    ${this.renderClientCoopReport(
      this.clientCoopPreview
    )}
  `;

  preview.scrollIntoView({
    behavior: 'smooth',
    block: 'start'
  });
}



renderClientCoopReport(report) {
  const clientName =
    this.clientAccount
      ?.business_name ||
    this.clientAccount
      ?.client_name ||
    'Client';

  const catalogs =
    Array.isArray(
      report?.catalogs
    )
      ? report.catalogs
      : [];

  const isAnnual =
    catalogs.length >= 9;

  return `
    <div
      class="aa-client-coop-document ${isAnnual ? 'is-annual' : ''}"
    >
      <div class="aa-client-coop-document__topbar"></div>

      <header class="aa-client-coop-document__masthead">
        <img
          src="${this.escapeCatalogHtml(
            window.AACOOPLogoUrl ||
            ''
          )}"
          alt="Artisan's Avenue to Marketing"
        >

        <span>
          CO-OP MARKETING SUMMARY
        </span>
      </header>

      <section class="aa-client-coop-document__heading">
        <h2>
          ${this.escapeCatalogHtml(
            report.reportTitle ||
            'Co-op Marketing Catalog Summary'
          )}
        </h2>

        <p>
          ${this.escapeCatalogHtml(
            clientName
          )}
          ${report.periodLabel
            ? ` · ${this.escapeCatalogHtml(
                report.periodLabel
              )}`
            : ''
          }
          ${report.manufacturer
            ? ` · ${this.escapeCatalogHtml(
                report.manufacturer
              )}`
            : ''
          }
        </p>
      </section>

      <div class="aa-client-coop-document__rule"></div>

      <section class="aa-client-coop-document__catalogs">
        ${catalogs.map((catalog) => {
          const month =
            this.formatCatalogMonth(
              catalog.catalog_month
            );

          const storedPdfUrl =
            catalog.catalog_pdf_storage_path &&
            window.AASupabaseClient
              ?.getCatalogPdfPublicUrl
              ? window.AASupabaseClient
                  .getCatalogPdfPublicUrl({
                    path:
                      catalog
                        .catalog_pdf_storage_path
                  })
              : '';

          const viewUrl =
            storedPdfUrl ||
            String(
              catalog.catalog_url ||
              ''
            ).trim();

          return `
            <article class="aa-client-coop-document__catalog">
              <div class="aa-client-coop-document__cover">
                ${catalog.cover_image_url
                  ? `
                    <img
                      src="${this.escapeCatalogHtml(
                        catalog.cover_image_url
                      )}"
                      alt="${this.escapeCatalogHtml(
                        month
                      )}"
                    >
                  `
                  : `
                    <div>Catalog</div>
                  `
                }
              </div>

              <div class="aa-client-coop-document__line">
                <strong>
                  ${this.escapeCatalogHtml(
                    month
                  )}
                </strong>

                ${viewUrl
                  ? `
                    <a
                      href="${this.escapeCatalogHtml(
                        viewUrl
                      )}"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View Catalog
                    </a>
                  `
                  : ''
                }
              </div>
            </article>
          `;
        }).join('')}
      </section>

      <footer class="aa-client-coop-document__footer">
        Artisan’s Avenue to Marketing
      </footer>

      <div class="aa-client-coop-document__bottombar"></div>
    </div>
  `;
}

async getCoopPdfImage(
  url,
  {
    format = 'JPEG',
    quality = 0.92
  } = {}
) {
  if (!url) {
    return null;
  }

  const response =
    await fetch(
      url,
      {
        cache: 'force-cache'
      }
    );

  if (!response.ok) {
    throw new Error(
      `Unable to load report image: ${response.status}`
    );
  }

  const blob =
    await response.blob();

  const bitmap =
    await createImageBitmap(
      blob
    );

  const maxDimension = 1500;

  const scale =
    Math.min(
      1,
      maxDimension /
        Math.max(
          bitmap.width,
          bitmap.height
        )
    );

  const width =
    Math.max(
      1,
      Math.round(
        bitmap.width *
        scale
      )
    );

  const height =
    Math.max(
      1,
      Math.round(
        bitmap.height *
        scale
      )
    );

  const canvas =
    document.createElement(
      'canvas'
    );

  canvas.width = width;
  canvas.height = height;

  const context =
    canvas.getContext(
      '2d',
      {
        alpha:
          format === 'PNG'
      }
    );

  if (
    format !== 'PNG'
  ) {
    context.fillStyle =
      '#ffffff';

    context.fillRect(
      0,
      0,
      width,
      height
    );
  }

  context.drawImage(
    bitmap,
    0,
    0,
    width,
    height
  );

  bitmap.close?.();

  return {
    dataUrl:
      canvas.toDataURL(
        format === 'PNG'
          ? 'image/png'
          : 'image/jpeg',
        quality
      ),
    width,
    height,
    format
  };
}

getClientCoopViewUrl(
  catalog
) {
  const storedPdfUrl =
    catalog
      ?.catalog_pdf_storage_path &&
    window.AASupabaseClient
      ?.getCatalogPdfPublicUrl
      ? window.AASupabaseClient
          .getCatalogPdfPublicUrl({
            path:
              catalog
                .catalog_pdf_storage_path
          })
      : '';

  return (
    storedPdfUrl ||
    String(
      catalog?.catalog_url ||
      ''
    ).trim()
  );
}

async downloadClientCoopReport(
  report
) {
  const jsPDF =
    window.jspdf?.jsPDF;

  if (!jsPDF) {
    throw new Error(
      'PDF generator is unavailable.'
    );
  }

  const clientName =
    this.clientAccount
      ?.business_name ||
    this.clientAccount
      ?.client_name ||
    'Client';

  const catalogs =
    Array.isArray(
      report?.catalogs
    )
      ? report.catalogs
      : [];

  if (!catalogs.length) {
    throw new Error(
      'No catalogs are available for this report.'
    );
  }

  const doc =
    new jsPDF({
      orientation: 'portrait',
      unit: 'pt',
      format: 'letter',
      compress: true
    });

  const pageWidth = 612;
  const pageHeight = 792;

  const teal = [
    104,
    144,
    151
  ];

  const charcoal = [
    48,
    53,
    55
  ];

  const muted = [
    119,
    115,
    109
  ];

  const cream = [
    245,
    241,
    232
  ];

  const logo =
    await this
      .getCoopPdfImage(
        window.AACOOPLogoUrl ||
        '',
        {
          format: 'PNG'
        }
      )
      .catch(
        () => null
      );

  const coverImages =
    await Promise.all(
      catalogs.map(
        async (catalog) => {
          const coverUrl =
            String(
              catalog.cover_image_url ||
              ''
            ).trim();

          if (!coverUrl) {
            return null;
          }

          return this
            .getCoopPdfImage(
              coverUrl,
              {
                format: 'JPEG',
                quality: 0.9
              }
            )
            .catch(
              () => null
            );
        }
      )
    );

  const pages = [];

  for (
    let index = 0;
    index < catalogs.length;
    index += 12
  ) {
    pages.push({
      catalogs:
        catalogs.slice(
          index,
          index + 12
        ),
      images:
        coverImages.slice(
          index,
          index + 12
        )
    });
  }

  pages.forEach(
    (page, pageIndex) => {
      if (pageIndex > 0) {
        doc.addPage();
      }

      doc.setFillColor(
        ...teal
      );

      doc.rect(
        0,
        0,
        pageWidth,
        5,
        'F'
      );

      doc.rect(
        0,
        pageHeight - 5,
        pageWidth,
        5,
        'F'
      );

      if (logo) {
        const logoWidth =
          218;

        const logoHeight =
          logoWidth *
          logo.height /
          logo.width;

        doc.addImage(
          logo.dataUrl,
          logo.format,
          38,
          24,
          logoWidth,
          logoHeight,
          undefined,
          'FAST'
        );
      }

      doc.setTextColor(
        ...teal
      );

      doc.setFont(
        'helvetica',
        'bold'
      );

      doc.setFontSize(8);

      doc.text(
        'CO-OP MARKETING SUMMARY',
        pageWidth - 38,
        46,
        {
          align: 'right'
        }
      );

      doc.setDrawColor(
        221,
        216,
        206
      );

      doc.setLineWidth(0.7);

      doc.line(
        38,
        82,
        pageWidth - 38,
        82
      );

      doc.setTextColor(
        ...charcoal
      );

      doc.setFont(
        'times',
        'normal'
      );

      doc.setFontSize(20);

      doc.text(
        report.reportTitle ||
          'Co-op Marketing Catalog Summary',
        pageWidth / 2,
        108,
        {
          align: 'center',
          maxWidth: 500
        }
      );

      const metaParts = [
        clientName,
        report.periodLabel ||
          ''
      ];

      if (
        report.manufacturer
      ) {
        metaParts.push(
          report.manufacturer
        );
      }

      doc.setTextColor(
        ...muted
      );

      doc.setFont(
        'helvetica',
        'normal'
      );

      doc.setFontSize(8.5);

      doc.text(
        metaParts
          .filter(Boolean)
          .join('  ·  '),
        pageWidth / 2,
        127,
        {
          align: 'center',
          maxWidth: 500
        }
      );

      doc.setFillColor(
        ...teal
      );

      doc.rect(
        38,
        139,
        pageWidth - 76,
        2,
        'F'
      );

      const coverWidth = 95;
      const coverHeight =
        coverWidth *
        16 / 9;

      const columnGap = 20;
      const rowGap = 12;

      const rowHeight =
        coverHeight +
        17 +
        rowGap;

      const startY = 156;

      page.catalogs
        .forEach(
          (catalog, index) => {
            const row =
              Math.floor(
                index / 4
              );

            const col =
              index % 4;

            const rowStart =
              row * 4;

            const rowCount =
              Math.min(
                4,
                page.catalogs.length -
                rowStart
              );

            const totalWidth =
              rowCount *
                coverWidth +
              Math.max(
                0,
                rowCount - 1
              ) *
                columnGap;

            const startX =
              (
                pageWidth -
                totalWidth
              ) / 2;

            const x =
              startX +
              col *
              (
                coverWidth +
                columnGap
              );

            const y =
              startY +
              row *
              rowHeight;

            doc.setDrawColor(
              210,
              204,
              194
            );

            doc.setLineWidth(
              0.7
            );

            doc.setFillColor(
              ...cream
            );

            doc.roundedRect(
              x,
              y,
              coverWidth,
              coverHeight,
              2,
              2,
              'FD'
            );

            const image =
              page.images[
                index
              ];

            if (image) {
              doc.addImage(
                image.dataUrl,
                image.format,
                x,
                y,
                coverWidth,
                coverHeight,
                undefined,
                'FAST'
              );
            } else {
              doc.setTextColor(
                ...muted
              );

              doc.setFont(
                'helvetica',
                'normal'
              );

              doc.setFontSize(
                8
              );

              doc.text(
                'Catalog',
                x +
                  coverWidth /
                    2,
                y +
                  coverHeight /
                    2,
                {
                  align:
                    'center'
                }
              );
            }

            const month =
              this.formatCatalogMonth(
                catalog
                  .catalog_month
              );

            const lineY =
              y +
              coverHeight +
              11;

            doc.setTextColor(
              ...charcoal
            );

            doc.setFont(
              'times',
              'bold'
            );

            doc.setFontSize(
              8.2
            );

            doc.text(
              month,
              x,
              lineY
            );

            const viewUrl =
              this.getClientCoopViewUrl(
                catalog
              );

            if (viewUrl) {
              const label =
                'View Catalog';

              doc.setFont(
                'helvetica',
                'bold'
              );

              doc.setFontSize(
                6.9
              );

              doc.setTextColor(
                ...teal
              );

              const linkWidth =
                doc.getTextWidth(
                  label
                );

              const linkX =
                x +
                coverWidth -
                linkWidth;

              doc.text(
                label,
                linkX,
                lineY
              );

              doc.setDrawColor(
                170,
                192,
                196
              );

              doc.setLineWidth(
                0.5
              );

              doc.line(
                linkX,
                lineY + 1.5,
                linkX +
                  linkWidth,
                lineY + 1.5
              );

              doc.link(
                linkX,
                lineY - 7,
                linkWidth,
                10,
                {
                  url: viewUrl
                }
              );
            }
          }
        );

      doc.setDrawColor(
        221,
        216,
        206
      );

      doc.setLineWidth(
        0.7
      );

      doc.line(
        38,
        758,
        pageWidth - 38,
        758
      );

      doc.setTextColor(
        125,
        133,
        134
      );

      doc.setFont(
        'helvetica',
        'bold'
      );

      doc.setFontSize(
        6.8
      );

      doc.text(
        'ARTISAN’S AVENUE TO MARKETING',
        pageWidth / 2,
        770,
        {
          align: 'center'
        }
      );
    }
  );

  const safeClientName =
    String(clientName)
      .trim()
      .replace(
        /[^a-zA-Z0-9]+/g,
        '-'
      )
      .replace(
        /^-+|-+$/g,
        ''
      ) ||
    'Client';

  const safePeriod =
    String(
      report.periodLabel ||
      'Co-op'
    )
      .trim()
      .replace(
        /[^a-zA-Z0-9]+/g,
        '-'
      )
      .replace(
        /^-+|-+$/g,
        ''
      );

  doc.save(
    `${safeClientName}-${safePeriod}-Co-op-Marketing-Summary.pdf`
  );
}

getCatalogYear(record) {
  const value = String(record?.catalog_month || '');

  return /^\d{4}/.test(value)
    ? value.slice(0, 4)
    : '';
}

formatCatalogMonth(value) {
  if (!value) {
    return '';
  }

  const [year, month] =
    String(value).split('-').map(Number);

  if (!year || !month) {
    return '';
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC'
  }).format(
    new Date(Date.UTC(year, month - 1, 1))
  );
}

formatCatalogPublicationDate(value) {
  if (!value) {
    return '';
  }

  const parts = String(value)
    .slice(0, 10)
    .split('-')
    .map(Number);

  if (
    parts.length !== 3 ||
    !parts[0] ||
    !parts[1] ||
    !parts[2]
  ) {
    return '';
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC'
  }).format(
    new Date(
      Date.UTC(
        parts[0],
        parts[1] - 1,
        parts[2]
      )
    )
  );
}

formatCatalogViews(value) {
  const number = Number(value);

  if (!Number.isFinite(number) || number < 0) {
    return '';
  }

  return `${new Intl.NumberFormat('en-US').format(number)} views`;
}

populateCatalogFilters() {
  const yearSelect =
    this.root.querySelector('[data-aa-catalog-year]');

  const brandSelect =
    this.root.querySelector('[data-aa-catalog-brand]');

  const years = [
    ...new Set(
      this.catalogRecords
        .map((record) => this.getCatalogYear(record))
        .filter(Boolean)
    )
  ].sort((a, b) => Number(b) - Number(a));

  const brands = [
    ...new Set(
      this.catalogRecords.flatMap((record) =>
        Array.isArray(record.brands)
          ? record.brands
              .map((brand) => String(brand).trim())
              .filter(Boolean)
          : []
      )
    )
  ].sort((a, b) =>
    a.localeCompare(b, undefined, {
      sensitivity: 'base'
    })
  );

  if (yearSelect) {
    yearSelect.innerHTML = `
      <option value="">All Years</option>

      ${years.map((year) => `
        <option
          value="${this.escapeCatalogHtml(year)}"
          ${
            this.catalogYearFilter === year
              ? 'selected'
              : ''
          }
        >
          ${this.escapeCatalogHtml(year)}
        </option>
      `).join('')}
    `;
  }

  if (brandSelect) {
    brandSelect.innerHTML = `
      <option value="">All Brands</option>

      ${brands.map((brand) => `
        <option
          value="${this.escapeCatalogHtml(brand)}"
          ${
            this.catalogBrandFilter === brand
              ? 'selected'
              : ''
          }
        >
          ${this.escapeCatalogHtml(brand)}
        </option>
      `).join('')}
    `;
  }
}

async loadCatalogHistory() {
  const container =
    this.root.querySelector('[data-aa-catalog-history]');

  if (!container) {
    return;
  }

  if (this.catalogLoadError) {
    container.innerHTML = `
      <div class="aa-client-catalog-state">
        We couldn't load your catalogs right now.
        Please try again later.
      </div>
    `;

    return;
  }

  this.populateCatalogFilters();
  this.populateClientCoopFilters();
  this.renderCatalogRecords();
}

renderCatalogRecords() {
  const container =
    this.root.querySelector('[data-aa-catalog-history]');

  if (!container) {
    return;
  }

  const selectedBrand =
    String(this.catalogBrandFilter || '')
      .trim()
      .toLowerCase();

  const records = this.catalogRecords.filter((record) => {
    const yearMatches =
      !this.catalogYearFilter ||
      this.getCatalogYear(record) ===
        this.catalogYearFilter;

    const brands = Array.isArray(record.brands)
      ? record.brands
      : [];

    const brandMatches =
      !selectedBrand ||
      brands.some(
        (brand) =>
          String(brand)
            .trim()
            .toLowerCase() === selectedBrand
      );

    return yearMatches && brandMatches;
  });

  if (!records.length) {
    container.innerHTML = `
      <div class="aa-client-catalog-state">
        No catalogs are available yet.
      </div>
    `;

    return;
  }

  container.innerHTML = records.map((catalog) => {
    const brands = Array.isArray(catalog.brands)
      ? catalog.brands
          .map((brand) => String(brand).trim())
          .filter(Boolean)
      : [];

    const title =
      catalog.catalog_title || 'Catalog';

    const month =
      this.formatCatalogMonth(
        catalog.catalog_month
      );

    const publicationDate =
      this.formatCatalogPublicationDate(
        catalog.publication_date
      );

    const views =
      this.formatCatalogViews(
        catalog.catalog_views
      );

    const cover =
      String(catalog.cover_image_url || '').trim();

    const catalogUrl =
      String(catalog.catalog_url || '').trim();

    const storedPdfUrl =
      catalog.catalog_pdf_storage_path &&
      window.AASupabaseClient
        ?.getCatalogPdfPublicUrl
        ? window.AASupabaseClient
            .getCatalogPdfPublicUrl({
              path:
                catalog
                  .catalog_pdf_storage_path
            })
        : '';

    return `
      <article class="aa-client-catalog-card">

        <div class="aa-client-catalog-card__cover">
          ${
            cover
              ? `
                <img
                  src="${this.escapeCatalogHtml(cover)}"
                  alt="${this.escapeCatalogHtml(title)} cover"
                  loading="lazy"
                  data-aa-catalog-cover-image
                >

                <div
                  class="aa-client-catalog-card__placeholder"
                  data-aa-catalog-cover-placeholder
                  hidden
                >
                  Catalog
                </div>
              `
              : `
                <div
                  class="aa-client-catalog-card__placeholder"
                >
                  Catalog
                </div>
              `
          }
        </div>

        <div class="aa-client-catalog-card__body">

          ${
            month
              ? `
                <p class="aa-client-catalog-card__month">
                  ${this.escapeCatalogHtml(month)}
                </p>
              `
              : ''
          }

          <h3>
            ${this.escapeCatalogHtml(title)}
          </h3>

          ${
            publicationDate
              ? `
                <p class="aa-client-catalog-card__published">
                  Published ${this.escapeCatalogHtml(publicationDate)}
                </p>
              `
              : ''
          }

          ${
            views
              ? `
                <p class="aa-client-catalog-card__views">
                  ${this.escapeCatalogHtml(views)}
                </p>
              `
              : ''
          }

          ${
            brands.length
              ? `
                <div
                  class="aa-client-catalog-card__brands"
                  aria-label="Brands represented"
                >
                  ${brands.map((brand) => `
                    <span class="aa-client-catalog-brand">
                      ${this.escapeCatalogHtml(brand)}
                    </span>
                  `).join('')}
                </div>
              `
              : ''
          }

          ${
            storedPdfUrl
              ? `
                <a
                  class="aa-button aa-button--small"
                  href="${this.escapeCatalogHtml(storedPdfUrl)}"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View Catalog
                </a>
              `
              : catalogUrl
                ? `
                  <a
                    class="aa-button aa-button--small"
                    href="${this.escapeCatalogHtml(catalogUrl)}"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View Catalog
                  </a>
                `
                : ''
          }

        </div>
      </article>
    `;
  }).join('');

  container
    .querySelectorAll('[data-aa-catalog-cover-image]')
    .forEach((image) => {
      image.addEventListener('error', () => {
        image.hidden = true;

        const placeholder =
          image.parentElement?.querySelector(
            '[data-aa-catalog-cover-placeholder]'
          );

        if (placeholder) {
          placeholder.hidden = false;
        }
      });
    });
}

renderAccountIdentity(verifiedIdentity = null) {
  const workspace = verifiedIdentity || window.AAWorkspaceContext.get();
  const user = workspace?.user;

  if (!user) return;

  const metadata = user.user_metadata || {};

  const email =
    user.email ||
    '';

  const fallbackName =
    email
      ? email.split('@')[0]
      : 'Account';

  const name =
    metadata.full_name ||
    metadata.name ||
    metadata.display_name ||
    metadata.first_name ||
    fallbackName;

  const roleLabels = {
    aa_admin: 'AA Admin',
    aa_team: 'AA Team',
    client_owner: 'Client Owner',
    client_employee: 'Client Employee'
  };

  let role =
    roleLabels[workspace.role] ||
    workspace.role ||
    'Workspace User';

  const adminClientId =
    window.AAAdminWorkspaceView?.getClientId?.();

  if (workspace.isInternal && adminClientId) {
    role = `${role} · Admin View`;
  }

  const nameEl =
    this.root.querySelector('[data-aa-workspace-user-name]');

  const emailEl =
    this.root.querySelector('[data-aa-workspace-user-email]');

  const roleEl =
    this.root.querySelector('[data-aa-workspace-user-role]');

  if (nameEl) {
    nameEl.textContent = name;
  }

  if (emailEl) {
    emailEl.textContent = email;
  }

  if (roleEl) {
    roleEl.textContent = role;
  }

  const avatarEl = this.root.querySelector('[data-aa-workspace-avatar]');
  if (avatarEl) {
    avatarEl.textContent = String(name || '').trim().charAt(0).toUpperCase();
  }

  const accountEl = this.root.querySelector('[data-aa-workspace-account]');
  if (accountEl) accountEl.setAttribute('aria-busy', 'false');
}

 async loadDashboardAccountAlerts() {
  const dashboardPage =
    this.root.querySelector(
      '[data-aa-dashboard]'
    );

  if (
    !dashboardPage ||
    !window.AAPlaybookService
  ) {
    return;
  }

  const workspace =
    window.AAWorkspaceContext.get();

  if (!workspace?.clientId) {
    return;
  }

  const actionCenter =
    this.root.querySelector(
      '[data-aa-dashboard-actions]'
    );

  const actionCount =
    this.root.querySelector(
      '[data-aa-dashboard-action-count]'
    );

  if (!actionCenter || !actionCount) {
    return;
  }

  const [
    contractResult,
    vendorResult,
    invoiceResult
  ] = await Promise.allSettled([
    window.AAPlaybookService
      .getClientContracts?.({
        clientId: workspace.clientId
      }) || [],
    window.AAPlaybookService
      .getClientVendorBrands?.({
        clientId: workspace.clientId
      }) || [],
    window.AAPlaybookService
      .getMyWaveInvoices?.({
        clientId: workspace.clientId
      }) || {
        success: false,
        invoices: []
      }
  ]);

  const contracts =
    contractResult.status === 'fulfilled' &&
    Array.isArray(contractResult.value)
      ? contractResult.value
      : [];

  const vendors =
    vendorResult.status === 'fulfilled' &&
    Array.isArray(vendorResult.value)
      ? vendorResult.value
      : [];

  const invoicePayload =
    invoiceResult.status === 'fulfilled'
      ? invoiceResult.value
      : null;

  const invoices =
    invoicePayload?.success &&
    Array.isArray(invoicePayload.invoices)
      ? invoicePayload.invoices
      : [];

  const pendingContract =
    contracts
      .filter((contract) =>
        [
          'sent',
          'pending_signature',
          'viewed'
        ].includes(contract.status)
      )
      .sort((a, b) =>
        String(
          b.sentForSignatureAt ||
          b.updatedAt ||
          b.createdAt ||
          ''
        ).localeCompare(
          String(
            a.sentForSignatureAt ||
            a.updatedAt ||
            a.createdAt ||
            ''
          )
        )
      )[0] || null;

  const vendorNeedsAccess =
    vendors.filter((vendor) =>
      [
        'needed',
        'problem'
      ].includes(vendor.accessStatus)
    );

  const vendorRequested =
    vendors.filter(
      (vendor) =>
        vendor.accessStatus === 'requested'
    );

  const today =
    new Date();

  today.setHours(0, 0, 0, 0);

  const overdueInvoices =
    invoices.filter((invoice) => {
      const status =
        String(invoice.status || '')
          .trim()
          .toLowerCase();

      const amountDue =
        this.parseInvoiceAmount(
          invoice?.amountDue?.value
        );

      const dueDate =
        invoice.dueDate
          ? new Date(
              String(invoice.dueDate)
                .slice(0, 10) +
              'T00:00:00'
            )
          : null;

      return (
        status === 'overdue' ||
        (
          amountDue > 0 &&
          dueDate &&
          !Number.isNaN(dueDate.getTime()) &&
          dueDate < today &&
          status !== 'paid'
        )
      );
    });

  const accountAlerts = [];

  const formatNames = (items) => {
    const names =
      items
        .slice(0, 3)
        .map((item) =>
          this.escapeCatalogHtml(
            item.vendorName
          )
        )
        .join(', ');

    const extraCount =
      Math.max(items.length - 3, 0);

    return `${names}${extraCount
      ? ` and ${extraCount} more`
      : ''
    }`;
  };

  if (pendingContract) {
    accountAlerts.push(`
      <a
        href="/pages/workspace-account#billing-agreements"
        class="aa-dashboard-action-item aa-dashboard-action-item--contract"
      >
        <div class="aa-dashboard-action-item__body">
          <strong>
            Agreement awaiting signature
          </strong>

          <p>
            Your new Artisan's Avenue agreement has been sent for signature. Check your email for the Google eSignature request.
          </p>
        </div>

        <span class="aa-dashboard-action-item__meta">
          Action needed
        </span>
      </a>
    `);
  }

  if (overdueInvoices.length) {
    const overdueBalance =
      overdueInvoices.reduce(
        (total, invoice) =>
          total +
          this.parseInvoiceAmount(
            invoice?.amountDue?.value
          ),
        0
      );

    const currency =
      String(
        overdueInvoices[0]?.currency ||
        ''
      ).trim();

    let overdueBalanceLabel =
      `$${overdueBalance.toFixed(2)}`;

    if (currency) {
      try {
        overdueBalanceLabel =
          new Intl.NumberFormat(
            'en-US',
            {
              style: 'currency',
              currency
            }
          ).format(overdueBalance);
      } catch {
        overdueBalanceLabel =
          `$${overdueBalance.toFixed(2)}`;
      }
    }

    const singleOverdue =
      overdueInvoices.length === 1
        ? overdueInvoices[0]
        : null;

    const invoiceAlertHref =
      singleOverdue?.viewUrl ||
      '/pages/workspace-account#billing-agreements';

    const invoiceAlertExternal =
      Boolean(singleOverdue?.viewUrl);

    accountAlerts.push(`
      <a
        href="${this.escapeCatalogHtml(
          invoiceAlertHref
        )}"
        class="aa-dashboard-action-item aa-dashboard-action-item--invoice"
        ${invoiceAlertExternal
          ? 'target="_blank" rel="noopener noreferrer"'
          : ''
        }
      >
        <div class="aa-dashboard-action-item__body">
          <strong>
            ${overdueInvoices.length === 1
              ? 'Overdue invoice'
              : `${overdueInvoices.length} overdue invoices`
            }
          </strong>

          <p>
            ${overdueInvoices.length === 1
              ? `Invoice #${this.escapeCatalogHtml(
                  overdueInvoices[0].invoiceNumber || ''
                )} has an overdue balance of ${this.escapeCatalogHtml(
                  overdueBalanceLabel
                )}.`
              : `There is ${this.escapeCatalogHtml(
                  overdueBalanceLabel
                )} currently overdue across ${overdueInvoices.length} invoices.`
            }
          </p>
        </div>

        <span class="aa-dashboard-action-item__meta">
          ${invoiceAlertExternal
            ? 'View Invoice'
            : 'Review Invoices'
          }
        </span>
      </a>
    `);
  }

  if (vendorNeedsAccess.length) {
    const names =
      formatNames(vendorNeedsAccess);

    accountAlerts.push(`
      <a
        href="/pages/workspace-account#brand-assets"
        class="aa-dashboard-action-item aa-dashboard-action-item--vendor"
      >
        <div class="aa-dashboard-action-item__body">
          <strong>
            Marketing access needed
          </strong>

          <p>
            Artisan's Avenue still needs marketing access for ${names}.
          </p>
        </div>

        <span class="aa-dashboard-action-item__meta">
          Needs attention
        </span>
      </a>
    `);
  }

  if (vendorRequested.length) {
    const names =
      formatNames(vendorRequested);

    accountAlerts.push(`
      <a
        href="/pages/workspace-account#brand-assets"
        class="aa-dashboard-action-item aa-dashboard-action-item--vendor"
      >
        <div class="aa-dashboard-action-item__body">
          <strong>
            Marketing access requested
          </strong>

          <p>
            Marketing access has been requested for ${names} and is still pending.
          </p>
        </div>

        <span class="aa-dashboard-action-item__meta">
          In progress
        </span>
      </a>
    `);
  }

  if (!accountAlerts.length) {
    return;
  }

  const existingItems =
    actionCenter.querySelectorAll(
      '.aa-dashboard-action-item'
    ).length;

  if (!existingItems) {
    actionCenter.innerHTML = '';
  }

  actionCenter.insertAdjacentHTML(
    'afterbegin',
    accountAlerts.join('')
  );

  actionCount.hidden = false;
  actionCount.textContent =
    String(
      existingItems +
      accountAlerts.length
    );
}

 async loadDashboardComingUp() {
  const container =
    this.root.querySelector(
      '[data-aa-dashboard-coming-up]'
    );

  if (
    !container ||
    !window.AAPlaybookService
  ) {
    return;
  }

  const workspace =
    window.AAWorkspaceContext.get();

  if (!workspace?.clientId) {
    return;
  }

  const [
    windowResult,
    contractResult
  ] = await Promise.allSettled([
    window.AAPlaybookService
      .getPlaybookWindow(
        workspace.clientId
      ),
    window.AAPlaybookService
      .getClientContracts?.({
        clientId: workspace.clientId
      }) || []
  ]);

  const windowData =
    windowResult.status === 'fulfilled'
      ? windowResult.value
      : null;

  const contracts =
    contractResult.status === 'fulfilled' &&
    Array.isArray(contractResult.value)
      ? contractResult.value
      : [];

  const today =
    new Date();

  today.setHours(0, 0, 0, 0);

  const items = [];

  const pushDatedItem = ({
    date,
    eyebrow,
    title,
    detail,
    href,
    meta
  }) => {
    if (!date) return;

    const parsed =
      new Date(
        String(date).slice(0, 10) +
        'T00:00:00'
      );

    if (
      Number.isNaN(parsed.getTime()) ||
      parsed < today
    ) {
      return;
    }

    items.push({
      date: parsed,
      eyebrow,
      title,
      detail,
      href,
      meta
    });
  };

  const nextPlaybook =
    windowData?.nextPlaybook || null;

  if (nextPlaybook) {
    const dueDate =
      new Date(
        Number(nextPlaybook.year),
        Number(nextPlaybook.month) - 2,
        15
      );

    const monthLabel =
      `${this.getMonthName(
        nextPlaybook.month
      )} ${nextPlaybook.year}`;

    items.push({
      date:
        dueDate >= today
          ? dueDate
          : today,
      eyebrow: 'Next Playbook',
      title: monthLabel,
      detail:
        dueDate >= today
          ? `Planning due ${dueDate.toLocaleDateString(
              'en-US',
              {
                month: 'long',
                day: 'numeric'
              }
            )}`
          : 'Ready for planning',
      href:
        nextPlaybook.id
          ? `/pages/workspace-current-playbook?playbook=${encodeURIComponent(
              nextPlaybook.id
            )}`
          : '/pages/workspace-previous-playbooks',
      meta:
        window.AAPlaybookService
          .formatPlaybookStatus?.(
            window.AAPlaybookService
              .resolvePlaybookStatus?.(
                nextPlaybook
              ) ||
            nextPlaybook.status ||
            'draft'
          ) ||
        'Upcoming'
    });
  }

  contracts.forEach((contract) => {
    const status =
      this.resolveContractDisplayStatus(
        contract
      );

    if (
      status === 'signed' &&
      contract.effectiveDate
    ) {
      pushDatedItem({
        date: contract.effectiveDate,
        eyebrow: 'Agreement',
        title:
          contract.title ||
          'New agreement begins',
        detail:
          `Effective ${this.formatDate(
            contract.effectiveDate
          )}`,
        href:
          '/pages/workspace-account#billing-agreements',
        meta: 'Starts Soon'
      });
    }

    if (
      ['active','renewal_due']
        .includes(status)
    ) {
      if (contract.renewalDate) {
        pushDatedItem({
          date: contract.renewalDate,
          eyebrow: 'Agreement',
          title: 'Renewal approaching',
          detail:
            `Renewal date ${this.formatDate(
              contract.renewalDate
            )}`,
          href:
            '/pages/workspace-account#billing-agreements',
          meta: 'Renewal'
        });
      } else if (contract.expirationDate) {
        pushDatedItem({
          date: contract.expirationDate,
          eyebrow: 'Agreement',
          title: 'Agreement end date',
          detail:
            `Ends ${this.formatDate(
              contract.expirationDate
            )}`,
          href:
            '/pages/workspace-account#billing-agreements',
          meta: 'Upcoming'
        });
      }
    }
  });

  items.sort(
    (a, b) =>
      a.date.getTime() -
      b.date.getTime()
  );

  const visible =
    items.slice(0, 3);

  if (!visible.length) {
    container.innerHTML = `
      <div class="aa-dashboard-empty-state">
        <div class="aa-dashboard-empty-state__content">
          <strong>Nothing scheduled yet.</strong>
          <p>
            Upcoming account milestones will appear here.
          </p>
        </div>
      </div>
    `;
    return;
  }

  container.innerHTML =
    visible.map((item) => `
      <a
        href="${this.escapeCatalogHtml(
          item.href || '#'
        )}"
        class="aa-dashboard-coming-up__item"
      >
        <div class="aa-dashboard-coming-up__copy">
          <span>
            ${this.escapeCatalogHtml(
              item.eyebrow || 'Coming Up'
            )}
          </span>

          <strong>
            ${this.escapeCatalogHtml(
              item.title || ''
            )}
          </strong>

          <p>
            ${this.escapeCatalogHtml(
              item.detail || ''
            )}
          </p>
        </div>

        <small>
          ${this.escapeCatalogHtml(
            item.meta || 'Upcoming'
          )}
        </small>
      </a>
    `).join('');
}

 async loadDashboard() {
  const dashboardPage =
    this.root.querySelector('[data-aa-dashboard]');

  if (!dashboardPage) {
    return;
  }

  const workspace = window.AAWorkspaceContext.get();

  if (!workspace?.playbookId || !window.AAPlaybookService) {
    const monthValue =
      this.root.querySelector('[data-aa-dashboard-month]');

    const statusValue =
      this.root.querySelector('[data-aa-dashboard-status]');

    const accessValue =
      this.root.querySelector('[data-aa-dashboard-access]');

    const actionButton =
      this.root.querySelector('[data-aa-dashboard-action]');

    const heroActionButton =
      this.root.querySelector('[data-aa-dashboard-hero-action]');

    const lastSavedLabel =
      this.root.querySelector('[data-aa-dashboard-last-saved-label]');

    const lastSavedMessage =
      this.root.querySelector('[data-aa-dashboard-last-saved-message]');

    const lastSavedValue =
      this.root.querySelector('[data-aa-dashboard-last-saved-value]');

    if (monthValue) {
      monthValue.textContent = 'No current Playbook';
    }

    if (statusValue) {
      statusValue.textContent = '—';
    }

    if (accessValue) {
      accessValue.textContent = '—';
    }

    if (actionButton) {
      actionButton.textContent = 'View Playbooks';
      actionButton.href = '/pages/workspace-previous-playbooks';
    }

    if (heroActionButton) {
      heroActionButton.textContent = 'View Playbooks';
      heroActionButton.href = '/pages/workspace-previous-playbooks';
    }

    if (lastSavedLabel) {
      lastSavedLabel.textContent = 'No Playbook Yet';
    }

    if (lastSavedMessage) {
      lastSavedMessage.textContent =
        'A Playbook has not been created for this client yet.';
    }

    if (lastSavedValue) {
      lastSavedValue.textContent = '—';
    }

    return;
  }

  const monthValue = this.root.querySelector('[data-aa-dashboard-month]');
  const statusValue = this.root.querySelector('[data-aa-dashboard-status]');
  const accessValue = this.root.querySelector('[data-aa-dashboard-access]');
const dueDateValue = this.root.querySelector('[data-aa-dashboard-due-date]');
const actionButton = this.root.querySelector('[data-aa-dashboard-action]');
  const heroActionButton = this.root.querySelector('[data-aa-dashboard-hero-action]');

  const nextMonthValue = this.root.querySelector('[data-aa-dashboard-next-month]');
  const nextStatusValue = this.root.querySelector('[data-aa-dashboard-next-status]');
  const nextMessageValue = this.root.querySelector('[data-aa-dashboard-next-message]');

  const accountStatusValue = this.root.querySelector('[data-aa-dashboard-account-status]');
  const invoiceStatusValue = this.root.querySelector('[data-aa-dashboard-invoice-status]');

  const lastSavedLabel = this.root.querySelector('[data-aa-dashboard-last-saved-label]');
const lastSavedMessage = this.root.querySelector('[data-aa-dashboard-last-saved-message]');
const lastSavedValue = this.root.querySelector('[data-aa-dashboard-last-saved-value]');
const activityTime = this.root.querySelector('[data-aa-dashboard-activity-time]');

const actionCenter =
  this.root.querySelector('[data-aa-dashboard-actions]');

const actionCount =
  this.root.querySelector('[data-aa-dashboard-action-count]');

const progressValue =
  this.root.querySelector('[data-aa-dashboard-progress]');

const progressBar =
  this.root.querySelector('[data-aa-dashboard-progress-bar]');

const progressPercent =
  this.root.querySelector('[data-aa-dashboard-progress-percent]');

const [
  dashboard,
  windowData,
  completion
] = await Promise.all([
  window.AAPlaybookService.getPlaybookDashboardById({
    playbookId: workspace.playbookId,
    clientId: workspace.clientId
  }),

  window.AAPlaybookService.getPlaybookWindow(
    workspace.clientId
  ),

  window.AAPlaybookService.getPlaybookCompletion({
    playbookId: workspace.playbookId
  })
]);

if (!dashboard) return;

  const playbook = dashboard.playbook;

  const primaryAction =
  window.AAPlaybookService.getPlaybookPrimaryAction(playbook);

const resolvedStatus = primaryAction.status;
const canEdit = primaryAction.editable;

const playbookIsComplete =
  completion.totalSections > 0 &&
  completion.completedSections >= completion.totalSections;

if (progressValue) {
  progressValue.textContent =
    `${completion.completedSections} of ${completion.totalSections} sections complete`;
}

if (progressBar) {
  progressBar.style.width =
    `${completion.percentage}%`;

  progressBar.setAttribute(
    'aria-valuenow',
    String(completion.percentage)
  );
}

if (progressPercent) {
  progressPercent.textContent =
    `${completion.percentage}%`;
}

if (monthValue) {
    monthValue.textContent =
      `${this.getMonthName(playbook.month)} ${playbook.year}`;
  }

  if (statusValue) {
    statusValue.classList.add('aa-status-pill');
    statusValue.dataset.status = resolvedStatus;
    statusValue.textContent =
      window.AAPlaybookService.formatPlaybookStatus(resolvedStatus);
  }

  if (accessValue) {
  accessValue.textContent = canEdit
    ? 'Editable'
    : 'Read Only';
}

const playbookDueDate = new Date(
  Number(playbook.year),
  Number(playbook.month) - 2,
  15,
  23,
  59,
  59,
  999
);

if (dueDateValue) {
  dueDateValue.textContent = playbookDueDate.toLocaleDateString(
    'en-US',
    {
      month: 'long',
      day: 'numeric'
    }
  );
}

if (actionCenter && actionCount) {
  const now = new Date();

const warningStartDate = new Date(
  Number(playbook.year),
  Number(playbook.month) - 2,
  10,
  0,
  0,
  0,
  0
);

const todayStart = new Date(
  now.getFullYear(),
  now.getMonth(),
  now.getDate()
);

const dueDayStart = new Date(
  playbookDueDate.getFullYear(),
  playbookDueDate.getMonth(),
  playbookDueDate.getDate()
);

const daysRemaining = Math.round(
  (dueDayStart.getTime() - todayStart.getTime()) /
  (1000 * 60 * 60 * 24)
);

const warningIsActive =
  now >= warningStartDate &&
  now <= playbookDueDate;

  if (
  canEdit &&
  !playbookIsComplete &&
  warningIsActive &&
  daysRemaining >= 0
) {
    actionCount.hidden = false;
    actionCount.textContent = '1';

    const deadlineLabel =
      daysRemaining === 0
        ? 'Due today'
        : daysRemaining === 1
          ? 'Due tomorrow'
          : `Due in ${daysRemaining} days`;

    actionCenter.innerHTML = `
      <a
        href="/pages/workspace-current-playbook"
        class="aa-dashboard-action-item"
      >
        <div class="aa-dashboard-action-item__body">
          <strong>September Playbook is due soon</strong>
          <p>
            Complete your Playbook by
            ${playbookDueDate.toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric'
            })}.
          </p>
        </div>

        <span class="aa-dashboard-action-item__meta">
          ${deadlineLabel}
        </span>
      </a>
    `;
  } else {
    actionCount.hidden = true;
    actionCount.textContent = '0';

    actionCenter.innerHTML = `
      <div class="aa-dashboard-empty-state">
        <div class="aa-dashboard-empty-state__content">
          <strong>You’re all caught up.</strong>
          <p>Nothing needs your attention right now.</p>
        </div>
      </div>
    `;
  }
}

  if (actionButton) {
  actionButton.textContent = primaryAction.shortLabel;
  actionButton.href = primaryAction.href;
}

if (heroActionButton) {
  heroActionButton.textContent = primaryAction.label;
  heroActionButton.href = primaryAction.href;
}

  const activityMonth =
  `${this.getMonthName(playbook.month)} Playbook`;

if (dashboard.lastSavedAt) {
  if (lastSavedLabel) {
    lastSavedLabel.textContent =
      `${activityMonth} updated`;
  }

  if (lastSavedMessage) {
    lastSavedMessage.textContent =
      'Changes were saved to your Playbook.';
  }

  if (activityTime) {
    activityTime.textContent =
      new Date(dashboard.lastSavedAt).toLocaleString(
        'en-US',
        {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit'
        }
      );
  }
} else {
  if (lastSavedLabel) {
    lastSavedLabel.textContent =
      `${activityMonth} created`;
  }

  if (lastSavedMessage) {
    lastSavedMessage.textContent =
      'Your Playbook is ready for planning.';
  }

  if (activityTime) {
    activityTime.textContent = playbook.created_at
      ? new Date(playbook.created_at).toLocaleDateString(
          'en-US',
          {
            month: 'short',
            day: 'numeric'
          }
        )
      : '';
  }
}

  if (lastSavedValue) {
    lastSavedValue.textContent = dashboard.lastSavedAt
      ? new Date(dashboard.lastSavedAt).toLocaleString()
      : 'Not saved yet';
  }

  const nextPlaybook = windowData?.nextPlaybook || null;

  if (nextPlaybook) {

    if (nextMonthValue) {
      nextMonthValue.textContent =
        `${this.getMonthName(nextPlaybook.month)} ${nextPlaybook.year}`;
    }

    if (nextStatusValue) {
      nextStatusValue.textContent =
        window.AAPlaybookService.formatPlaybookStatus(
          window.AAPlaybookService.resolvePlaybookStatus(nextPlaybook)
        );
    }

    if (nextMessageValue) {
      nextMessageValue.textContent =
        'Your next scheduled Playbook is ready.';
    }

  } else {

    if (nextMonthValue) {
      nextMonthValue.textContent =
        'No upcoming Playbook';
    }

    if (nextStatusValue) {
      nextStatusValue.textContent = '—';
    }

    if (nextMessageValue) {
      nextMessageValue.textContent =
        'A future Playbook has not been created yet.';
    }

  }

  if (this.clientAccount) {

    if (accountStatusValue) {
      accountStatusValue.textContent =
        this.clientAccount.status || 'Active';
    }

    if (invoiceStatusValue) {
      invoiceStatusValue.textContent =
        this.clientAccount.invoice_status || 'Current';
    }

  }
}



  async loadAccount() {
    const accountPage = this.root.querySelector('[data-aa-account-page]');
    if (!accountPage || !window.AAPlaybookService) return;

    const workspace = window.AAWorkspaceContext.get();

   const account = this.clientAccount;

    const playbookResult =
  await window.AAPlaybookService.loadPlaybookById({
    playbookId: workspace.playbookId,
    clientId: workspace.clientId
  });

const playbook = playbookResult?.playbook;

const playbookStatus = playbook
  ? window.AAPlaybookService.resolvePlaybookStatus(playbook)
  : 'draft';

    const setText = (selector, value) => {
      const el = this.root.querySelector(selector);
      if (el) el.textContent = value || 'Not set';
    };


    setText('[data-aa-account-business-name]', account?.business_name || account?.client_name || 'Client Workspace');
    setText('[data-aa-account-client-id]', workspace.clientId);
    setText('[data-aa-account-playbook-id]', workspace.playbookId);
    setText('[data-aa-account-workspace-status]', 'Active');
    setText('[data-aa-account-playbook-month]', this.getMonthName(workspace.month) || workspace.month);
    setText('[data-aa-account-playbook-year]', workspace.year);
    setText(
      '[data-aa-account-playbook-status]',
      window.AAPlaybookService.formatPlaybookStatus(playbookStatus)
    );
    setText('[data-aa-account-status]', account?.status || 'Active');
    setText('[data-aa-account-invoice-status]', account?.invoice_status || 'Current');
  }
async loadMarketingPlan() {
  const workspace = window.AAWorkspaceContext.get();

  if (!workspace?.clientId || !window.AAPlaybookService) return;

  const card = this.root.querySelector('[data-aa-marketing-plan-card]');
  const titleValue = this.root.querySelector('[data-aa-marketing-plan-title]');
  const introValue = this.root.querySelector('[data-aa-marketing-plan-intro]');
  const statusValue = this.root.querySelector('[data-aa-marketing-plan-status]');
  const sectionsValue = this.root.querySelector('[data-aa-marketing-plan-sections]');
  const servicesValue = this.root.querySelector('[data-aa-marketing-plan-services]');

  if (!card) return;

  this.marketingPlan = await window.AAPlaybookService.getMarketingPlanByClient({
    clientId: workspace.clientId
  });

  const plan = this.marketingPlan?.plan || null;
  const sections = this.marketingPlan?.sections || [];
  const services = this.marketingPlan?.services || [];

  if (!plan) {
    if (titleValue) titleValue.textContent = 'Your Custom Marketing Plan';
    if (introValue) introValue.textContent = 'Your custom marketing plan has not been assigned yet.';
    if (statusValue) statusValue.textContent = 'Not assigned';
    if (sectionsValue) sectionsValue.textContent = '0';
    if (servicesValue) servicesValue.textContent = '0';
    return;
  }

  if (titleValue) {
    titleValue.textContent = plan.name || 'Your Custom Marketing Plan';
  }

  if (introValue) {
    introValue.textContent =
      plan.introText ||
      plan.intro_text ||
      'Your custom marketing plan is ready.';
  }

  if (statusValue) {
    statusValue.textContent = plan.status || 'Active';
  }

  if (sectionsValue) {
    sectionsValue.textContent = String(sections.length);
  }

  if (servicesValue) {
    servicesValue.textContent = String(services.length);
  }
}

async loadMarketingPackage() {
  const workspace = window.AAWorkspaceContext.get();

  if (!workspace?.clientId || !window.AAPlaybookService) return;

  const container =
    this.root.querySelector(
      '[data-aa-marketing-package]'
    );

  if (!container) return;

  const [marketingPackage, requests] =
    await Promise.all([
      window.AAPlaybookService
        .getClientMarketingPackage({
          clientId: workspace.clientId,
          month: workspace.month,
          year: workspace.year
        }),
      window.AAPlaybookService
        .getPackageChangeRequests?.({
          clientId: workspace.clientId
        }) || []
    ]);

  this.marketingPackage =
    marketingPackage || {
      services: []
    };

  this.packageChangeRequests =
    Array.isArray(requests)
      ? requests
      : [];

  const services =
    Array.isArray(this.marketingPackage?.services)
      ? this.marketingPackage.services
      : [];

  if (!services.length) {
    container.innerHTML = `
      <div class="aa-account-package-empty">
        <strong>Your package details are being prepared.</strong>
        <span>Your included recurring services will appear here.</span>
      </div>
    `;

    this.renderPackageChangeCenter();
    return;
  }

  container.innerHTML =
    services.map((service) => {
      const included =
        Number.isFinite(Number(service.included))
          ? Number(service.included)
          : service.allowance || '—';

      const unit =
        service.unit || 'included';

      const category =
        service.category || 'Included Service';

      return `
        <article class="aa-account-service-card">
          <div class="aa-account-service-card__top">
            <span class="aa-account-service-card__category">
              ${this.escapeCatalogHtml(category)}
            </span>

            <h4>
              ${this.escapeCatalogHtml(service.name || 'Service')}
            </h4>
          </div>

          <div class="aa-account-service-card__allowance">
            <strong>
              ${this.escapeCatalogHtml(included)}
            </strong>

            <span>
              ${this.escapeCatalogHtml(unit)}
            </span>
          </div>

          ${service.notes ? `
            <p class="aa-account-service-card__notes">
              ${this.escapeCatalogHtml(service.notes)}
            </p>
          ` : ''}
        </article>
      `;
    }).join('');

  this.renderPackageChangeCenter();
}

formatPackageChangeStatus(status) {
  const labels = {
    new: 'Submitted',
    reviewing: 'Under Review',
    approved: 'Approved',
    declined: 'Not Approved',
    completed: 'Completed'
  };

  return labels[status] || 'Submitted';
}

getPackageServiceCurrentValue(serviceName) {
  const services =
    Array.isArray(this.marketingPackage?.services)
      ? this.marketingPackage.services
      : [];

  const service =
    services.find((item) =>
      String(item.name || '') ===
      String(serviceName || '')
    );

  if (!service) {
    return '';
  }

  const included =
    Number.isFinite(Number(service.included))
      ? Number(service.included)
      : service.allowance || '';

  return [
    included,
    service.unit || ''
  ]
    .filter((value) =>
      String(value).trim()
    )
    .join(' ');
}

renderPackageChangeCenter() {
  const container =
    this.root.querySelector(
      '[data-aa-package-change-center]'
    );

  if (!container) return;

  const services =
    Array.isArray(this.marketingPackage?.services)
      ? this.marketingPackage.services
      : [];

  const requests =
    Array.isArray(this.packageChangeRequests)
      ? this.packageChangeRequests
      : [];

  const openRequests =
    requests.filter(
      (request) =>
        request.status === 'new'
    );

  const formMarkup =
    this.packageChangeOpen
      ? `
        <form
          class="aa-account-package-change-form"
          data-aa-package-change-form
          novalidate
        >
          <div class="aa-account-package-change-form__heading">
            <div>
              <span>Package Change Request</span>
              <strong>Tell us what you would like to change.</strong>
              <p>
                Submitting a request does not change your current package. Artisan's Avenue will review it with you first.
              </p>
            </div>

            <button
              type="button"
              class="aa-account-package-change-form__close"
              data-aa-close-package-change
            >
              Close
            </button>
          </div>

          <div class="aa-account-package-change-form__fields">
            <label>
              <span>Service</span>
              <select
                data-aa-package-change-service
                required
              >
                <option value="">Choose a service</option>
                ${services.map((service) => `
                  <option
                    value="${this.escapeCatalogHtml(
                      service.name || 'Service'
                    )}"
                  >
                    ${this.escapeCatalogHtml(
                      service.name || 'Service'
                    )}
                  </option>
                `).join('')}
                <option value="Other">Other</option>
              </select>
            </label>

            <label>
              <span>Current Package</span>
              <input
                type="text"
                value=""
                placeholder="Select a service"
                data-aa-package-change-current
                readonly
              >
            </label>

            <label>
              <span>Requested Change</span>
              <input
                type="text"
                placeholder="e.g. 5 social posts per week"
                data-aa-package-change-requested
                required
              >
            </label>

            <label class="aa-account-package-change-form__wide">
              <span>Anything we should know?</span>
              <textarea
                rows="3"
                placeholder="Optional"
                data-aa-package-change-note
              ></textarea>
            </label>
          </div>

          <div
            class="aa-account-package-change-form__message"
            data-aa-package-change-message
            aria-live="polite"
            ${this.packageChangeMessage ? '' : 'hidden'}
          >
            ${this.escapeCatalogHtml(
              this.packageChangeMessage
            )}
          </div>

          <div class="aa-account-package-change-form__footer">
            <button
              type="submit"
              class="aa-button aa-button--small"
              data-aa-submit-package-change
            >
              Submit Request
            </button>
          </div>
        </form>
      `
      : '';

  const requestMarkup =
    openRequests.length
      ? `
        <div class="aa-account-package-change-requests">
          <span class="aa-account-package-change-requests__label">
            Package Requests
          </span>

          ${openRequests.slice(0, 3)
            .map((request) => `
              <div class="aa-account-package-change-request">
                <div>
                  <strong>
                    ${this.escapeCatalogHtml(
                      request.serviceName
                    )}
                  </strong>

                  <small>
                    ${this.escapeCatalogHtml(
                      request.requestedPackageValue
                    )}
                  </small>
                </div>

                <span
                  data-status="${this.escapeCatalogHtml(
                    request.status
                  )}"
                >
                  ${this.escapeCatalogHtml(
                    this.formatPackageChangeStatus(
                      request.status
                    )
                  )}
                </span>
              </div>
            `).join('')}
        </div>
      `
      : '';

  container.innerHTML =
    `${formMarkup}${requestMarkup}`;
}

bindPackageChangeActions() {
  this.root.addEventListener(
    'click',
    (event) => {
      const openButton =
        event.target.closest(
          '[data-aa-open-package-change]'
        );

      const closeButton =
        event.target.closest(
          '[data-aa-close-package-change]'
        );

      if (openButton) {
        this.packageChangeOpen = true;
        this.packageChangeMessage = '';
        this.renderPackageChangeCenter();

        requestAnimationFrame(() => {
          this.root
            .querySelector(
              '[data-aa-package-change-service]'
            )
            ?.focus();
        });

        return;
      }

      if (closeButton) {
        this.packageChangeOpen = false;
        this.packageChangeMessage = '';
        this.renderPackageChangeCenter();
      }
    }
  );

  this.root.addEventListener(
    'change',
    (event) => {
      const serviceSelect =
        event.target.closest(
          '[data-aa-package-change-service]'
        );

      if (!serviceSelect) return;

      const currentInput =
        this.root.querySelector(
          '[data-aa-package-change-current]'
        );

      if (!currentInput) return;

      currentInput.value =
        serviceSelect.value === 'Other'
          ? 'Not currently included'
          : this.getPackageServiceCurrentValue(
              serviceSelect.value
            );
    }
  );

  this.root.addEventListener(
    'submit',
    async (event) => {
      const form =
        event.target.closest(
          '[data-aa-package-change-form]'
        );

      if (!form) return;

      event.preventDefault();

      const workspace =
        window.AAWorkspaceContext.get();

      const serviceName =
        form.querySelector(
          '[data-aa-package-change-service]'
        )?.value.trim() || '';

      const currentPackageValue =
        form.querySelector(
          '[data-aa-package-change-current]'
        )?.value.trim() || '';

      const requestedPackageValue =
        form.querySelector(
          '[data-aa-package-change-requested]'
        )?.value.trim() || '';

      const clientNote =
        form.querySelector(
          '[data-aa-package-change-note]'
        )?.value.trim() || '';

      const messageEl =
        form.querySelector(
          '[data-aa-package-change-message]'
        );

      const submitButton =
        form.querySelector(
          '[data-aa-submit-package-change]'
        );

      const setMessage = (
        message,
        state = ''
      ) => {
        if (!messageEl) return;

        messageEl.textContent = message;
        messageEl.hidden = !message;

        if (state) {
          messageEl.dataset.state = state;
        } else {
          delete messageEl.dataset.state;
        }
      };

      if (!serviceName) {
        setMessage(
          'Choose the service you would like to change.',
          'error'
        );
        return;
      }

      if (!requestedPackageValue) {
        setMessage(
          'Tell us what you would like changed.',
          'error'
        );
        return;
      }

      if (
        !workspace?.clientId ||
        !window.AAPlaybookService
          ?.submitPackageChangeRequest
      ) {
        setMessage(
          'Package requests are temporarily unavailable.',
          'error'
        );
        return;
      }

      if (submitButton) {
        submitButton.disabled = true;
        submitButton.textContent =
          'Submitting...';
      }

      setMessage('');

      try {
        const result =
          await window.AAPlaybookService
            .submitPackageChangeRequest({
              clientId:
                workspace.clientId,
              serviceName,
              currentPackageValue,
              requestedPackageValue,
              clientNote
            });

        if (!result?.success) {
          throw new Error(
            'Unable to submit package request.'
          );
        }

        this.packageChangeOpen = false;
        this.packageChangeMessage = '';

        await this.loadMarketingPackage();

        const refreshedCenter =
          this.root.querySelector(
            '[data-aa-package-change-center]'
          );

        if (refreshedCenter) {
          refreshedCenter.insertAdjacentHTML(
            'afterbegin',
            `
              <div class="aa-account-package-change-success">
                Request submitted. Your current package has not changed. Artisan's Avenue will review the request with you.
              </div>
            `
          );
        }
      } catch (error) {
        console.error(
          'Unable to submit package change request:',
          error
        );

        setMessage(
          'Unable to submit your request right now. Please try again.',
          'error'
        );

        if (submitButton) {
          submitButton.disabled = false;
          submitButton.textContent =
            'Submit Request';
        }
      }
    }
  );
}


formatConnectedAccountStatus(status) {
  const labels = {
    not_requested: 'Not Requested',
    requested: 'Access Needed',
    waiting_on_client: 'Waiting on You',
    provided: 'Provided · Awaiting Verification',
    verified: 'Connected',
    problem: 'Action Needed',
    not_required: 'Not Required'
  };

  return labels[status] || 'Not Requested';
}

bindConnectedAccountActions() {
  this.root.addEventListener(
    'click',
    async (event) => {
      const openButton =
        event.target.closest(
          '[data-aa-open-access-details]'
        );

      if (openButton) {
        await this.openAccessDetailsEditor({
          targetType:
            openButton.dataset
              .aaAccessTargetType,
          targetId:
            openButton.dataset
              .aaAccessTargetId,
          label:
            openButton.dataset
              .aaAccessTargetLabel
        });

        return;
      }

      if (
        event.target.closest(
          '[data-aa-access-details-close]'
        )
      ) {
        this.closeAccessDetailsEditor();
        return;
      }

      const revealButton =
        event.target.closest(
          '[data-aa-access-reveal-password]'
        );

      if (revealButton) {
        const submissionId =
          revealButton.dataset
            .aaAccessRevealPassword;

        if (
          !submissionId ||
          !window.AAPlaybookService
            ?.revealAccessPassword
        ) {
          return;
        }

        revealButton.disabled = true;
        revealButton.textContent =
          'Loading...';

        const result =
          await window.AAPlaybookService
            .revealAccessPassword({
              submissionId
            });

        revealButton.disabled = false;
        revealButton.textContent =
          'Reveal Password';

        const output =
          this.root.querySelector(
            '[data-aa-access-revealed-password]'
          );

        if (output) {
          output.hidden = false;
          output.textContent =
            result?.success
              ? (
                  result.password ||
                  'No password is stored.'
                )
              : 'Unable to reveal password.';
        }

        return;
      }

      const statusButton =
        event.target.closest(
          '[data-aa-access-mark-status]'
        );

      if (statusButton) {
        const submissionId =
          statusButton.dataset
            .aaAccessSubmissionId;

        const status =
          statusButton.dataset
            .aaAccessMarkStatus;

        if (
          !submissionId ||
          !status ||
          !window.AAPlaybookService
            ?.setAccessSubmissionStatus
        ) {
          return;
        }

        statusButton.disabled = true;

        const result =
          await window.AAPlaybookService
            .setAccessSubmissionStatus({
              submissionId,
              status
            });

        if (!result?.success) {
          statusButton.disabled = false;
          this.setAccessDetailsMessage(
            'Unable to update access status.',
            'error'
          );
          return;
        }

        this.closeAccessDetailsEditor();

        await Promise.all([
          this.loadConnectedAccounts(),
          this.loadBrandAssets()
        ]);

        return;
      }
    }
  );

  this.root.addEventListener(
    'submit',
    async (event) => {
      const form =
        event.target.closest(
          '[data-aa-access-details-form]'
        );

      if (!form) {
        return;
      }

      event.preventDefault();

      const target =
        this.accessDetailsTarget;

      if (
        !target?.targetType ||
        !target?.targetId ||
        !window.AAPlaybookService
          ?.submitAccessDetails
      ) {
        return;
      }

      const field = (name) =>
        form.querySelector(
          `[data-aa-access-field="${name}"]`
        );

      const submitButton =
        form.querySelector(
          '[data-aa-access-details-submit]'
        );

      if (submitButton) {
        submitButton.disabled = true;
        submitButton.textContent =
          'Saving...';
      }

      this.setAccessDetailsMessage('');

      const result =
        await window.AAPlaybookService
          .submitAccessDetails({
            targetType:
              target.targetType,
            targetId:
              target.targetId,
            loginUrl:
              field('loginUrl')
                ?.value.trim() || '',
            loginEmail:
              field('loginEmail')
                ?.value.trim() || '',
            loginUsername:
              field('loginUsername')
                ?.value.trim() || '',
            password:
              field('password')
                ?.value || '',
            mfaOwner:
              field('mfaOwner')
                ?.value.trim() || '',
            notes:
              field('notes')
                ?.value.trim() || ''
          });

      if (!result?.success) {
        if (submitButton) {
          submitButton.disabled = false;
          submitButton.textContent =
            'Save Access Details';
        }

        this.setAccessDetailsMessage(
          'Unable to save these access details. Please try again.',
          'error'
        );

        return;
      }

      this.closeAccessDetailsEditor();

      await Promise.all([
        this.loadConnectedAccounts(),
        this.loadBrandAssets()
      ]);
    }
  );
}

ensureAccessDetailsEditor() {
  let editor =
    this.root.querySelector(
      '[data-aa-access-details-editor]'
    );

  if (editor) {
    return editor;
  }

  editor =
    document.createElement('div');

  editor.className =
    'aa-access-details-overlay';

  editor.setAttribute(
    'data-aa-access-details-editor',
    ''
  );

  editor.hidden = true;

  editor.innerHTML = `
    <button
      type="button"
      class="aa-access-details-overlay__backdrop"
      data-aa-access-details-close
      aria-label="Close access details"
    ></button>

    <section
      class="aa-access-details-panel"
      role="dialog"
      aria-modal="true"
      aria-labelledby="aa-access-details-title"
    >
      <header class="aa-access-details-panel__header">
        <div>
          <p class="aa-card__label">
            Account Access
          </p>

          <h3 id="aa-access-details-title">
            Provide Access
          </h3>

          <p data-aa-access-details-subtitle>
            Add the information Artisan's Avenue needs.
          </p>
        </div>

        <button
          type="button"
          class="aa-access-details-panel__close"
          data-aa-access-details-close
          aria-label="Close"
        >
          ×
        </button>
      </header>

      <form data-aa-access-details-form>
        <div class="aa-access-details-grid">
          <label class="aa-access-details-field aa-access-details-field--wide">
            <span>Website / Login URL</span>
            <input
              type="url"
              data-aa-access-field="loginUrl"
              placeholder="https://"
            >
          </label>

          <label class="aa-access-details-field">
            <span>Login Email</span>
            <input
              type="email"
              data-aa-access-field="loginEmail"
            >
          </label>

          <label class="aa-access-details-field">
            <span>Username</span>
            <input
              type="text"
              data-aa-access-field="loginUsername"
              autocomplete="username"
            >
          </label>

          <label class="aa-access-details-field aa-access-details-field--wide">
            <span>Password</span>
            <input
              type="password"
              data-aa-access-field="password"
              autocomplete="new-password"
            >
            <small data-aa-access-password-help>
              Stored securely. Leave blank if no password is needed.
            </small>
          </label>

          <label class="aa-access-details-field aa-access-details-field--wide">
            <span>MFA / Verification Contact</span>
            <input
              type="text"
              data-aa-access-field="mfaOwner"
              placeholder="Who receives security codes?"
            >
          </label>

          <label class="aa-access-details-field aa-access-details-field--wide">
            <span>Notes</span>
            <textarea
              data-aa-access-field="notes"
              rows="3"
              placeholder="Anything else we need to know"
            ></textarea>
          </label>
        </div>

        <div
          class="aa-access-details-message"
          data-aa-access-details-message
          hidden
        ></div>

        <div
          class="aa-access-details-internal"
          data-aa-access-internal-controls
          hidden
        >
          <div>
            <strong>Internal Access Review</strong>
            <span>
              Passwords are only revealed to Artisan's Avenue team members.
            </span>
          </div>

          <div class="aa-access-details-internal__actions">
            <button
              type="button"
              class="aa-button aa-button--secondary aa-button--small"
              data-aa-access-reveal-password
              hidden
            >
              Reveal Password
            </button>

            <button
              type="button"
              class="aa-button aa-button--secondary aa-button--small"
              data-aa-access-mark-status="problem"
              hidden
            >
              Needs Attention
            </button>

            <button
              type="button"
              class="aa-button aa-button--small"
              data-aa-access-mark-status="verified"
              hidden
            >
              Mark Verified
            </button>
          </div>

          <div
            class="aa-access-details-revealed-password"
            data-aa-access-revealed-password
            hidden
          ></div>
        </div>

        <footer class="aa-access-details-panel__footer">
          <button
            type="button"
            class="aa-button aa-button--secondary"
            data-aa-access-details-close
          >
            Cancel
          </button>

          <button
            type="submit"
            class="aa-button aa-button--primary"
            data-aa-access-details-submit
          >
            Save Access Details
          </button>
        </footer>
      </form>
    </section>
  `;

  this.root.appendChild(editor);

  return editor;
}

async openAccessDetailsEditor({
  targetType,
  targetId,
  label
}) {
  if (
    !targetType ||
    !targetId ||
    !window.AAPlaybookService
      ?.getAccessSubmission
  ) {
    return;
  }

  const editor =
    this.ensureAccessDetailsEditor();

  const workspace =
    window.AAWorkspaceContext.get();

  editor.hidden = false;

  document.body.classList.add(
    'aa-modal-open'
  );

  this.accessDetailsTarget = {
    targetType,
    targetId,
    label:
      label || 'Account Access'
  };

  const title =
    editor.querySelector(
      '#aa-access-details-title'
    );

  const subtitle =
    editor.querySelector(
      '[data-aa-access-details-subtitle]'
    );

  if (title) {
    title.textContent =
      label || 'Provide Access';
  }

  if (subtitle) {
    subtitle.textContent =
      workspace?.isInternal
        ? 'Review or update the access information on file.'
        : 'Add or update the information Artisan\'s Avenue needs.';
  }

  const submission =
    await window.AAPlaybookService
      .getAccessSubmission({
        targetType,
        targetId
      });

  this.accessDetailsSubmission =
    submission?.success
      ? submission
      : null;

  const platformFallback =
    targetType === 'platform'
      ? this.connectedAccounts.find(
          (item) =>
            String(item.id) ===
            String(targetId)
        )
      : null;

  const setValue = (
    name,
    value
  ) => {
    const input =
      editor.querySelector(
        `[data-aa-access-field="${name}"]`
      );

    if (input) {
      input.value =
        value || '';
    }
  };

  setValue(
    'loginUrl',
    submission?.loginUrl || ''
  );

  setValue(
    'loginEmail',
    submission?.loginEmail ||
    platformFallback?.loginEmail ||
    ''
  );

  setValue(
    'loginUsername',
    submission?.loginUsername ||
    platformFallback?.loginUsername ||
    ''
  );

  setValue(
    'mfaOwner',
    submission?.mfaOwner ||
    platformFallback?.mfaOwner ||
    ''
  );

  setValue(
    'notes',
    submission?.notes || ''
  );

  setValue(
    'password',
    ''
  );

  const passwordHelp =
    editor.querySelector(
      '[data-aa-access-password-help]'
    );

  if (passwordHelp) {
    passwordHelp.textContent =
      submission?.hasPassword
        ? 'A password is securely stored. Enter a new password only if it has changed.'
        : 'Stored securely. Leave blank if no password is needed.';
  }

  const internalControls =
    editor.querySelector(
      '[data-aa-access-internal-controls]'
    );

  const revealButton =
    editor.querySelector(
      '[data-aa-access-reveal-password]'
    );

  const statusButtons =
    editor.querySelectorAll(
      '[data-aa-access-mark-status]'
    );

  if (internalControls) {
    internalControls.hidden =
      !workspace?.isInternal ||
      !submission?.submissionId;
  }

  if (revealButton) {
    revealButton.hidden =
      !workspace?.isInternal ||
      !submission?.submissionId ||
      !submission?.hasPassword;

    revealButton.dataset
      .aaAccessRevealPassword =
        submission?.submissionId || '';
  }

  statusButtons.forEach(
    (button) => {
      button.hidden =
        !workspace?.isInternal ||
        !submission?.submissionId;

      button.dataset
        .aaAccessSubmissionId =
          submission?.submissionId || '';
    }
  );

  const revealed =
    editor.querySelector(
      '[data-aa-access-revealed-password]'
    );

  if (revealed) {
    revealed.hidden = true;
    revealed.textContent = '';
  }

  this.setAccessDetailsMessage('');
}

closeAccessDetailsEditor() {
  const editor =
    this.root.querySelector(
      '[data-aa-access-details-editor]'
    );

  if (editor) {
    editor.hidden = true;
  }

  document.body.classList.remove(
    'aa-modal-open'
  );

  this.accessDetailsTarget = null;
  this.accessDetailsSubmission = null;
}

setAccessDetailsMessage(
  message = '',
  state = ''
) {
  const messageEl =
    this.root.querySelector(
      '[data-aa-access-details-message]'
    );

  if (!messageEl) {
    return;
  }

  messageEl.textContent =
    message;

  messageEl.hidden =
    !message;

  if (state) {
    messageEl.dataset.state =
      state;
  } else {
    delete messageEl.dataset.state;
  }
}

async loadConnectedAccounts() {
  const workspace =
    window.AAWorkspaceContext.get();

  const container =
    this.root.querySelector(
      '[data-aa-connected-accounts]'
    );

  if (
    !container ||
    !workspace?.clientId ||
    !window.AAPlaybookService
      ?.getClientAccessRequirements
  ) {
    return;
  }

  this.connectedAccounts =
    await window.AAPlaybookService
      .getClientAccessRequirements({
        clientId: workspace.clientId
      });

  const access =
    Array.isArray(this.connectedAccounts)
      ? this.connectedAccounts
          .filter(
            (item) =>
              item.isRequired !== false &&
              item.status !== 'not_required'
          )
      : [];

  if (!access.length) {
    container.innerHTML = `
      <div class="aa-account-access-card__placeholder">
        <strong>No action needed right now.</strong>
        <span>
          Your required account access will appear here when needed.
        </span>
      </div>
    `;
    return;
  }

  container.innerHTML =
    access.map((item) => {
      const actionNeeded =
        [
          'not_requested',
          'requested',
          'waiting_on_client',
          'problem'
        ].includes(item.status);

      const actionLabel =
        workspace?.isInternal
          ? 'Access Details'
          : (
              actionNeeded
                ? 'Provide Access'
                : 'Update Access'
            );

      return `
        <article
          class="aa-client-connected-account"
          data-status="${this.escapeCatalogHtml(item.status)}"
        >
          <div class="aa-client-connected-account__top">
            <div>
              <span>
                ${this.escapeCatalogHtml(item.platformName)}
              </span>

              <strong>
                ${this.escapeCatalogHtml(
                  this.formatConnectedAccountStatus(
                    item.status
                  )
                )}
              </strong>
            </div>

            <span class="aa-client-connected-account__status-dot"></span>
          </div>

          ${item.clientInstructions ? `
            <p class="aa-client-connected-account__instructions">
              ${this.escapeCatalogHtml(item.clientInstructions)}
            </p>
          ` : ''}

          <button
            type="button"
            class="aa-button aa-button--small"
            data-aa-open-access-details
            data-aa-access-target-type="platform"
            data-aa-access-target-id="${this.escapeCatalogHtml(item.id)}"
            data-aa-access-target-label="${this.escapeCatalogHtml(item.platformName)}"
          >
            ${actionLabel}
          </button>
        </article>
      `;
    }).join('');
}


parseInvoiceAmount(value) {
  if (
    value === null ||
    value === undefined ||
    value === ''
  ) {
    return 0;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value)
      ? value
      : 0;
  }

  const normalized =
    String(value)
      .trim()
      .replace(/,/g, '')
      .replace(/[^0-9.-]/g, '');

  const parsed =
    Number(normalized);

  return Number.isFinite(parsed)
    ? parsed
    : 0;
}

formatInvoiceMoney(invoice, fieldName) {
  const amount =
    invoice?.[fieldName] || {};

  const value =
    this.parseInvoiceAmount(
      amount?.value
    );

  const currency =
    String(
      invoice?.currency ||
      ''
    ).trim();

  if (currency) {
    try {
      return new Intl.NumberFormat(
        'en-US',
        {
          style: 'currency',
          currency
        }
      ).format(value);
    } catch {
      // Fall through to Wave's symbol/value.
    }
  }

  const symbol =
    String(amount?.symbol || '$');

  return `${symbol}${value.toFixed(2)}`;
}

async loadInvoiceCenter() {
  const workspace =
    window.AAWorkspaceContext.get();

  const container =
    this.root.querySelector(
      '[data-aa-invoice-center]'
    );

  if (!container) return;

  // Hide billing altogether when the signed-in client user lacks
  // Invoices View. The Edge Function must enforce this independently.
  const billingCard = container.closest('.aa-account-resource-card--billing');
  const clientDb = window.AASupabaseClient?.getClient?.();
  if (workspace?.clientId && clientDb) {
    const { data: mayViewInvoices, error: permissionError } = await clientDb.rpc(
      'aa_has_client_permission',
      { p_client_id: workspace.clientId, p_permission_key: 'client.invoices.view' }
    );
    if (permissionError || mayViewInvoices !== true) {
      if (billingCard) billingCard.hidden = true;
      return;
    }
    if (billingCard) billingCard.hidden = false;
  } else {
    if (billingCard) billingCard.hidden = true;
    return;
  }

  if (
    !workspace?.clientId ||
    !window.AAPlaybookService
      ?.getMyWaveInvoices
  ) {
    container.innerHTML = `
      <div class="aa-account-resource-card__quiet">
        Invoice information is temporarily unavailable.
      </div>
    `;
    return;
  }

  this.invoices = [];
  this.invoiceLoadError = '';

  try {
    const result =
      await window.AAPlaybookService
        .getMyWaveInvoices({
          clientId: workspace.clientId
        });

    if (!result?.success) {
      throw (
        result?.error ||
        new Error(
          'Invoice information is temporarily unavailable.'
        )
      );
    }

    this.invoices =
      Array.isArray(result.invoices)
        ? result.invoices
        : [];
  } catch (error) {
    console.error(
      'Unable to load Wave invoices:',
      error
    );

    this.invoiceLoadError =
      'Invoice information is temporarily unavailable. Please try again shortly.';
  }

  this.renderInvoiceCenter(container);
}

renderInvoiceCenter(container) {
  if (!container) return;

  if (this.invoiceLoadError) {
    container.innerHTML = `
      <div class="aa-account-invoice-state aa-account-invoice-state--error">
        ${this.escapeCatalogHtml(
          this.invoiceLoadError
        )}
      </div>
    `;
    return;
  }

  const invoices =
    Array.isArray(this.invoices)
      ? this.invoices
      : [];

  if (!invoices.length) {
    container.innerHTML = `
      <div class="aa-account-invoice-state">
        No invoices are currently available.
      </div>
    `;
    return;
  }

  const outstanding =
    invoices.reduce(
      (total, invoice) =>
        total +
        this.parseInvoiceAmount(
          invoice?.amountDue?.value
        ),
      0
    );

  const openInvoices =
    invoices.filter(
      (invoice) =>
        this.parseInvoiceAmount(
          invoice?.amountDue?.value
        ) > 0
    ).length;

  const firstInvoice =
    invoices[0] || null;

  const currency =
    String(
      firstInvoice?.currency ||
      ''
    ).trim();

  let outstandingLabel =
    `$${outstanding.toFixed(2)}`;

  if (currency) {
    try {
      outstandingLabel =
        new Intl.NumberFormat(
          'en-US',
          {
            style: 'currency',
            currency
          }
        ).format(outstanding);
    } catch {
      outstandingLabel =
        `$${outstanding.toFixed(2)}`;
    }
  }

  const visibleInvoices =
    invoices.slice(0, 6);

  const olderInvoices =
    invoices.slice(6);

  const renderInvoiceRow =
    (invoice) => `
      <div class="aa-account-invoice-row">
        <strong>
          ${invoice.invoiceNumber
            ? `#${this.escapeCatalogHtml(
                invoice.invoiceNumber
              )}`
            : 'Invoice'
          }
        </strong>

        <span>
          ${this.escapeCatalogHtml(
            this.formatDate(
              invoice.invoiceDate
            ) || '—'
          )}
        </span>

        <span>
          ${this.escapeCatalogHtml(
            this.formatInvoiceMoney(
              invoice,
              'total'
            )
          )}
        </span>

        <span>
          ${this.escapeCatalogHtml(
            this.formatInvoiceMoney(
              invoice,
              'amountDue'
            )
          )}
        </span>

        <span
          class="aa-account-invoice-status"
          data-status="${this.escapeCatalogHtml(
            String(
              invoice.status || ''
            ).toLowerCase()
          )}"
        >
          ${this.escapeCatalogHtml(
            invoice.status || '—'
          )}
        </span>

        <div class="aa-account-invoice-actions">
          ${invoice.viewUrl ? `
            <a
              href="${this.escapeCatalogHtml(
                invoice.viewUrl
              )}"
              target="_blank"
              rel="noopener noreferrer"
            >
              View Invoice
            </a>
          ` : ''}

          ${invoice.pdfUrl ? `
            <a
              href="${this.escapeCatalogHtml(
                invoice.pdfUrl
              )}"
              target="_blank"
              rel="noopener noreferrer"
            >
              PDF
            </a>
          ` : ''}
        </div>
      </div>
    `;

  container.innerHTML = `
    <div class="aa-account-invoice-summary">
      <div>
        <small>Outstanding Balance</small>
        <strong>
          ${this.escapeCatalogHtml(
            outstandingLabel
          )}
        </strong>
      </div>

      <div>
        <small>Open Invoices</small>
        <strong>${openInvoices}</strong>
      </div>

      <div>
        <small>Most Recent</small>
        <strong>
          ${firstInvoice?.invoiceNumber
            ? `#${this.escapeCatalogHtml(
                firstInvoice.invoiceNumber
              )}`
            : '—'
          }
        </strong>
      </div>
    </div>

    <div class="aa-account-invoice-list">
      <div class="aa-account-invoice-list__header">
        <span>Invoice</span>
        <span>Date</span>
        <span>Amount</span>
        <span>Balance</span>
        <span>Status</span>
        <span>Actions</span>
      </div>

      ${visibleInvoices
        .map(renderInvoiceRow)
        .join('')}

      ${olderInvoices.length ? `
        <details class="aa-account-invoice-history">
          <summary>
            View earlier invoices
            <span>
              ${olderInvoices.length}
            </span>
          </summary>

          <div class="aa-account-invoice-history__rows">
            ${olderInvoices
              .map(renderInvoiceRow)
              .join('')}
          </div>
        </details>
      ` : ''}
    </div>
  `;
}

async loadContractCenter() {
  const workspace = window.AAWorkspaceContext.get();

  if (
    !workspace?.clientId ||
    !window.AAPlaybookService?.getClientContracts
  ) {
    return;
  }

  const container =
    this.root.querySelector(
      '[data-aa-contract-center]'
    );

  if (!container) return;

  this.contracts =
    await window.AAPlaybookService.getClientContracts({
      clientId: workspace.clientId
    });

  const contracts =
    Array.isArray(this.contracts)
      ? this.contracts
      : [];

  if (!contracts.length) {
    container.innerHTML = `
      <div class="aa-account-contract-empty">
        <p class="aa-card__label">Agreements</p>
        <h3>No agreement on file</h3>
        <p>
          Your current agreement and renewal details will appear here once available.
        </p>
      </div>
    `;
    return;
  }

  const priority = {
    renewal_due: 1,
    pending_signature: 2,
    sent: 3,
    viewed: 4,
    signed: 5,
    active: 6,
    draft: 7,
    expired: 90,
    archived: 99
  };

  const sortedContracts =
    [...contracts].sort((a, b) => {
      const priorityDifference =
        (priority[a.status] || 50) -
        (priority[b.status] || 50);

      if (priorityDifference !== 0) {
        return priorityDifference;
      }

      return String(
        b.effectiveDate ||
        b.createdAt ||
        ''
      ).localeCompare(
        String(
          a.effectiveDate ||
          a.createdAt ||
          ''
        )
      );
    });

  const current =
    sortedContracts.find(
      (contract) =>
        ['active', 'renewal_due']
          .includes(
            this.resolveContractDisplayStatus(
              contract
            )
          )
    ) || null;

  const pending =
    sortedContracts.find(
      (contract) =>
        [
          'sent',
          'pending_signature',
          'viewed'
        ].includes(
          this.resolveContractDisplayStatus(
            contract
          )
        )
    ) || null;

  const upcomingSigned =
    sortedContracts.find(
      (contract) =>
        this.resolveContractDisplayStatus(
          contract
        ) === 'signed' &&
        contract.id !== current?.id
    ) || null;

  const draft =
    sortedContracts.find(
      (contract) =>
        contract.status === 'draft' &&
        contract.id !== current?.id
    ) || null;

  const pendingAgreement =
    pending ||
    upcomingSigned ||
    draft ||
    null;

  const history =
    sortedContracts.filter(
      (contract) =>
        contract.id !== current?.id &&
        contract.id !== pendingAgreement?.id
    );

  const renderHistory = () => {
    if (!history.length) return '';

    return `
      <details class="aa-account-contract-history">
        <summary>
          Past agreements
          <span>${history.length}</span>
        </summary>

        <div class="aa-account-contract-history__list">
          ${history.map((contract) => {
            const historyDocument =
              (contract.documents || []).find(
                (document) =>
                  document.type === 'signed_agreement'
              ) ||
              (contract.documents || []).find(
                (document) =>
                  document.type === 'agreement'
              ) ||
              (contract.documents || [])[0];

            return `
              <div class="aa-account-contract-history__row">
                <div>
                  <strong>
                    ${this.escapeCatalogHtml(
                      contract.title || 'Agreement'
                    )}
                  </strong>
                  <small>
                    ${this.escapeCatalogHtml(
                      this.formatContractStatus(
                        contract.status
                      )
                    )}
                    ${contract.effectiveDate ? `
                      ·
                      ${this.escapeCatalogHtml(
                        this.formatDate(
                          contract.effectiveDate
                        )
                      )}
                    ` : ''}
                  </small>
                </div>

                ${historyDocument?.signedUrl ? `
                  <a
                    href="${this.escapeCatalogHtml(
                      historyDocument.signedUrl
                    )}"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View PDF
                  </a>
                ` : ''}
              </div>
            `;
          }).join('')}
        </div>
      </details>
    `;
  };

  const renderAgreementCard = (
    contract,
    label,
    message = ''
  ) => {
    if (!contract) return '';

    const documents =
      Array.isArray(contract.documents)
        ? contract.documents
        : [];

    const primaryDocument =
      documents.find(
        (document) =>
          document.type === 'signed_agreement'
      ) ||
      documents.find(
        (document) =>
          document.type === 'agreement'
      ) ||
      documents[0] ||
      null;

    const effectiveDate =
      this.formatDate(contract.effectiveDate);

    const renewalDate =
      this.formatDate(contract.renewalDate);

    const expirationDate =
      this.formatDate(contract.expirationDate);

    const secondaryDateLabel =
      renewalDate
        ? 'Renewal'
        : (
            expirationDate
              ? 'Expiration'
              : 'Term'
          );

    const secondaryDateValue =
      renewalDate ||
      expirationDate ||
      'Ongoing';

    return `
      <div class="aa-account-contract-summary ${label === 'Agreement Awaiting Signature' ? 'aa-account-contract-summary--pending' : ''}">
        <div class="aa-account-contract-summary__top">
          <div>
            <p class="aa-card__label">${this.escapeCatalogHtml(label)}</p>
            <h3>
              ${this.escapeCatalogHtml(
                contract.title || 'Service Agreement'
              )}
            </h3>
          </div>

          <span
            class="aa-status-pill"
            data-status="${this.escapeCatalogHtml(
              contract.status || 'draft'
            )}"
          >
            ${this.escapeCatalogHtml(
              this.formatContractStatus(
                contract.status
              )
            )}
          </span>
        </div>

        <p class="aa-account-contract-summary__meta">
          ${this.escapeCatalogHtml(
            contract.type || 'Service Agreement'
          )}
          ${contract.version ? `
            <span>·</span>
            ${this.escapeCatalogHtml(contract.version)}
          ` : ''}
        </p>

        ${message ? `
          <p class="aa-account-contract-summary__notice">
            ${this.escapeCatalogHtml(message)}
          </p>
        ` : ''}

        <div class="aa-account-contract-summary__dates">
          <div>
            <small>Effective</small>
            <strong>
              ${effectiveDate || 'Not set'}
            </strong>
          </div>

          <div>
            <small>${secondaryDateLabel}</small>
            <strong>${secondaryDateValue}</strong>
          </div>
        </div>

        ${primaryDocument?.signedUrl ? `
          <div class="aa-account-contract-primary-document">
            <span>Agreement Document</span>
            <a
              href="${this.escapeCatalogHtml(
                primaryDocument.signedUrl
              )}"
              target="_blank"
              rel="noopener noreferrer"
            >
              <strong>
                ${this.escapeCatalogHtml(
                  primaryDocument.title ||
                  this.formatContractDocumentType(
                    primaryDocument.type
                  )
                )}
              </strong>
              <small>
                ${this.escapeCatalogHtml(
                  this.formatContractDocumentType(
                    primaryDocument.type
                  )
                )}
              </small>
              <b>
                ${primaryDocument.type === 'signed_agreement'
                  ? 'View Signed PDF'
                  : 'View PDF'
                }
              </b>
            </a>
          </div>
        ` : ''}
      </div>
    `;
  };

  const pendingMessage =
    pendingAgreement &&
    ['sent', 'pending_signature', 'viewed']
      .includes(pendingAgreement.status)
      ? 'This agreement has been sent for signature. Please check your email for the Google eSignature request.'
      : pendingAgreement?.status === 'signed'
        ? 'This agreement has been signed and is waiting for its effective date.'
        : '';

  container.innerHTML = `
    <div class="aa-account-contract-stack">
      ${pendingAgreement
        ? renderAgreementCard(
            pendingAgreement,
            ['sent', 'pending_signature', 'viewed']
              .includes(pendingAgreement.status)
              ? 'Agreement Awaiting Signature'
              : pendingAgreement.status === 'signed'
                ? 'Upcoming Agreement'
                : 'Pending Agreement',
            pendingMessage
          )
        : ''
      }

      ${current
        ? renderAgreementCard(
            current,
            'Current Agreement'
          )
        : `
          <div class="aa-account-contract-summary">
            <div class="aa-account-contract-summary__top">
              <div>
                <p class="aa-card__label">Current Agreement</p>
                <h3>No current agreement</h3>
              </div>
            </div>

            <p class="aa-account-contract-summary__meta">
              ${pendingAgreement
                ? 'A pending agreement is shown above.'
                : 'Previous agreements remain available below.'
              }
            </p>
          </div>
        `
      }

      ${renderHistory()}
    </div>
  `;

  return;

}

formatContractDocumentType(type) {
  const labels = {
    agreement: 'Agreement',
    signed_agreement: 'Signed Agreement',
    amendment: 'Amendment',
    audit_certificate: 'Signature Certificate',
    other: 'Document'
  };

  return labels[type] || 'Document';
}

formatBrandAssetType(type) {
  const labels = {
    logo: 'Logo',
    brand_color: 'Brand Color',
    font: 'Font',
    store_photo: 'Store Photo',
    team_photo: 'Owner / Team Photo'
  };

  return labels[type] || type || 'Asset';
}

formatVendorAccessStatus(status) {
  const labels = {
    unknown: 'Being Reviewed',
    available: 'Available',
    needed: 'Access Needed',
    requested: 'Requested',
    provided: 'Provided · Awaiting Verification',
    problem: 'Needs Attention'
  };

  return labels[status] || 'Being Reviewed';
}

setBrandAssetMessage(message = '', state = '') {
  const messageEl =
    this.root.querySelector(
      '[data-aa-client-brand-asset-message]'
    );

  if (!messageEl) return;

  messageEl.textContent = message;
  messageEl.hidden = !message;

  if (state) {
    messageEl.dataset.state = state;
  } else {
    delete messageEl.dataset.state;
  }
}

syncBrandAssetFormFields() {
  const form =
    this.root.querySelector(
      '[data-aa-client-brand-asset-form]'
    );

  if (!form) return;

  const type =
    form.querySelector(
      '[data-aa-client-brand-asset-type]'
    )?.value || 'logo';

  const valueInput =
    form.querySelector(
      '[data-aa-client-brand-asset-value]'
    );

  const valueLabel =
    form.querySelector(
      '[data-aa-client-brand-value-label]'
    );

  const fileInput =
    form.querySelector(
      '[data-aa-client-brand-asset-file]'
    );

  const valueRequired =
    ['brand_color','font']
      .includes(type);

  const fileRequired =
    ['logo','store_photo','team_photo']
      .includes(type);

  if (valueInput) {
    valueInput.disabled = !valueRequired;

    valueInput.placeholder =
      type === 'brand_color'
        ? 'e.g. #6F8790'
        : type === 'font'
          ? 'e.g. DM Serif Display'
          : 'Not used for this asset';
  }

  if (valueLabel) {
    valueLabel.textContent =
      type === 'brand_color'
        ? 'Brand Color Value'
        : type === 'font'
          ? 'Font Name'
          : 'Color / Font Value';
  }

  if (fileInput) {
    fileInput.disabled = !fileRequired;
  }
}

bindBrandAssetActions() {
  this.root.addEventListener(
    'change',
    (event) => {
      if (
        event.target.matches(
          '[data-aa-client-brand-asset-type]'
        )
      ) {
        this.setBrandAssetMessage('');
        this.syncBrandAssetFormFields();
      }
    }
  );

  this.syncBrandAssetFormFields();

  this.root.addEventListener(
    'submit',
    async (event) => {
      const form =
        event.target.closest(
          '[data-aa-client-brand-asset-form]'
        );

      if (!form) return;

      event.preventDefault();

      const workspace =
        window.AAWorkspaceContext.get();

      const clientId =
        workspace?.clientId;

      if (!clientId) return;

      const type =
        form.querySelector(
          '[data-aa-client-brand-asset-type]'
        )?.value || 'logo';

      const title =
        form.querySelector(
          '[data-aa-client-brand-asset-title]'
        )?.value.trim() || '';

      const value =
        form.querySelector(
          '[data-aa-client-brand-asset-value]'
        )?.value.trim() || '';

      const description =
        form.querySelector(
          '[data-aa-client-brand-asset-description]'
        )?.value.trim() || '';

      const file =
        form.querySelector(
          '[data-aa-client-brand-asset-file]'
        )?.files?.[0] || null;

      this.setBrandAssetMessage('');

      if (!title) {
        this.setBrandAssetMessage(
          'Enter a title for this brand asset.',
          'error'
        );
        return;
      }

      const fileRequired =
        ['logo','store_photo','team_photo']
          .includes(type);

      if (fileRequired && !file) {
        this.setBrandAssetMessage(
          'Choose a file for this asset.',
          'error'
        );
        return;
      }

      if (
        ['brand_color','font'].includes(type) &&
        !value
      ) {
        this.setBrandAssetMessage(
          type === 'brand_color'
            ? 'Enter the brand color value.'
            : 'Enter the font name.',
          'error'
        );
        return;
      }

      const saveButton =
        form.querySelector(
          '[data-aa-client-brand-asset-save]'
        );

      if (saveButton) {
        saveButton.disabled = true;
        saveButton.textContent = 'Saving...';
      }

      try {
        let upload = {
          success: true,
          path: '',
          fileName: ''
        };

        if (file) {
          upload =
            await window.AAPlaybookService
              .uploadBrandAssetFile({
                clientId,
                file
              });

          if (!upload?.success) {
            throw (
              upload?.error ||
              new Error('Unable to upload file.')
            );
          }
        }

        const result =
          await window.AAPlaybookService
            .createBrandAsset({
              clientId,
              type,
              title,
              fileName: upload.fileName,
              storagePath: upload.path,
              value,
              description,
              displayOrder:
                this.brandAssets.length
            });

        if (!result?.success) {
          throw (
            result?.error ||
            new Error('Unable to save asset.')
          );
        }

        form.reset();
        this.syncBrandAssetFormFields();

        this.setBrandAssetMessage(
          'Brand asset added.',
          'success'
        );

        await this.loadBrandAssets();

        if (saveButton) {
          saveButton.disabled = false;
          saveButton.textContent = 'Add Asset';
        }
      } catch (error) {
        console.error(
          'Unable to save brand asset:',
          error
        );

        this.setBrandAssetMessage(
          'Unable to save this brand asset. Please check the fields and try again.',
          'error'
        );

        if (saveButton) {
          saveButton.disabled = false;
          saveButton.textContent = 'Add Asset';
        }
      }
    }
  );

  this.root.addEventListener(
    'click',
    async (event) => {
      const button =
        event.target.closest(
          '[data-aa-client-archive-brand-asset]'
        );

      if (!button) return;

      const assetId =
        button.dataset
          .aaClientArchiveBrandAsset;

      if (
        button.dataset.confirmRemove !== 'true'
      ) {
        button.dataset.confirmRemove = 'true';
        button.textContent = 'Confirm Remove';
        button.classList.add('is-confirming');

        window.setTimeout(() => {
          if (
            button.isConnected &&
            button.dataset.confirmRemove === 'true'
          ) {
            delete button.dataset.confirmRemove;
            button.textContent = 'Remove Asset';
            button.classList.remove('is-confirming');
          }
        }, 5000);

        return;
      }

      button.disabled = true;
      button.textContent = 'Removing...';

      this.setBrandAssetMessage('');

      try {
        const result =
          await window.AAPlaybookService
            .archiveBrandAsset({
              assetId
            });

        if (!result?.success) {
          throw (
            result?.error ||
            new Error('Unable to remove asset.')
          );
        }

        this.setBrandAssetMessage(
          'Brand asset removed.',
          'success'
        );

        await this.loadBrandAssets();
      } catch (error) {
        console.error(
          'Unable to archive brand asset:',
          error
        );

        delete button.dataset.confirmRemove;
        button.disabled = false;
        button.textContent = 'Remove Asset';
        button.classList.remove('is-confirming');

        this.setBrandAssetMessage(
          'Unable to remove this brand asset. Please try again.',
          'error'
        );
      }
    }
  );
}

async loadBrandAssets() {
  const workspace =
    window.AAWorkspaceContext.get();

  if (
    !workspace?.clientId ||
    !window.AAPlaybookService
  ) {
    return;
  }

  const assetContainer =
    this.root.querySelector(
      '[data-aa-brand-assets]'
    );

  const vendorContainer =
    this.root.querySelector(
      '[data-aa-client-vendor-access]'
    );

  if (!assetContainer && !vendorContainer) {
    return;
  }

  const [assets, vendors] =
    await Promise.all([
      window.AAPlaybookService
        .getClientBrandAssets({
          clientId: workspace.clientId
        }),
      window.AAPlaybookService
        .getClientVendorBrands({
          clientId: workspace.clientId
        })
    ]);

  this.brandAssets =
    Array.isArray(assets)
      ? assets
      : [];

  this.vendorBrands =
    Array.isArray(vendors)
      ? vendors
      : [];

  if (assetContainer) {
    if (!this.brandAssets.length) {
      assetContainer.innerHTML = `
        <div class="aa-account-brand-empty">
          <strong>No brand assets uploaded yet.</strong>
          <span>
            Upload only the items you want Artisan's Avenue to use.
          </span>
        </div>
      `;
    } else {
      assetContainer.innerHTML =
        this.brandAssets.map((asset) => {
          const visual =
            ['logo','store_photo','team_photo']
              .includes(asset.type) &&
            asset.signedUrl;

          return `
            <article class="aa-account-brand-asset">
              ${visual ? `
                <a
                  class="aa-account-brand-asset__preview"
                  href="${this.escapeCatalogHtml(asset.signedUrl)}"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src="${this.escapeCatalogHtml(asset.signedUrl)}"
                    alt="${this.escapeCatalogHtml(asset.title)}"
                    loading="lazy"
                  >
                </a>
              ` : `
                <div class="aa-account-brand-asset__value">
                  ${this.escapeCatalogHtml(
                    asset.value ||
                    this.formatBrandAssetType(asset.type)
                  )}
                </div>
              `}

              <div class="aa-account-brand-asset__body">
                <span>
                  ${this.escapeCatalogHtml(
                    this.formatBrandAssetType(asset.type)
                  )}
                </span>

                <strong>
                  ${this.escapeCatalogHtml(asset.title)}
                </strong>

                ${asset.value ? `
                  <small>
                    ${this.escapeCatalogHtml(asset.value)}
                  </small>
                ` : ''}

                <button
                  type="button"
                  data-aa-client-archive-brand-asset="${this.escapeCatalogHtml(asset.id)}"
                >
                  Remove Asset
                </button>
              </div>
            </article>
          `;
        }).join('');
    }
  }

  if (vendorContainer) {
    if (!this.vendorBrands.length) {
      vendorContainer.innerHTML = `
        <div class="aa-account-brand-empty">
          <strong>No brands are on file yet.</strong>
          <span>
            Artisan's Avenue will add the brands your business carries.
          </span>
        </div>
      `;
    } else {
      const attentionVendors =
        this.vendorBrands.filter(
          (vendor) =>
            vendor.accessStatus !== 'available'
        );

      const availableVendors =
        this.vendorBrands.filter(
          (vendor) =>
            vendor.accessStatus === 'available'
        );

      vendorContainer.innerHTML = `
        ${attentionVendors.length ? `
          <div class="aa-account-vendor-attention">
            <span class="aa-account-vendor-group-label">
              Needs Attention
            </span>

            <div class="aa-account-vendor-list aa-account-vendor-list--attention">
              ${attentionVendors.map((vendor) => `
                <div
                  class="aa-account-vendor-row"
                  data-status="${this.escapeCatalogHtml(vendor.accessStatus)}"
                >
                  <span class="aa-account-vendor-row__dot"></span>

                  <div class="aa-account-vendor-row__copy">
                    <strong>
                      ${this.escapeCatalogHtml(vendor.vendorName)}
                    </strong>

                    <span>
                      ${this.escapeCatalogHtml(
                        this.formatVendorAccessStatus(
                          vendor.accessStatus
                        )
                      )}
                    </span>
                  </div>

                  <button
                    type="button"
                    class="aa-button aa-button--secondary aa-button--small"
                    data-aa-open-access-details
                    data-aa-access-target-type="vendor"
                    data-aa-access-target-id="${this.escapeCatalogHtml(vendor.relationshipId)}"
                    data-aa-access-target-label="${this.escapeCatalogHtml(vendor.vendorName)}"
                  >
                    ${vendor.accessStatus === 'provided'
                      ? 'Update Access'
                      : 'Provide Access'
                    }
                  </button>
                </div>
              `).join('')}
            </div>
          </div>
        ` : ''}

        ${availableVendors.length ? `
          <div class="aa-account-vendor-available">
            <span class="aa-account-vendor-group-label">
              Available Marketing Access
            </span>

            <div class="aa-account-vendor-pills">
              ${availableVendors.map((vendor) => `
                <span class="aa-account-vendor-pill">
                  ${this.escapeCatalogHtml(vendor.vendorName)}
                </span>
              `).join('')}
            </div>
          </div>
        ` : ''}
      `;
    }
  }
}

resolveContractDisplayStatus(contract) {
  if (!contract) return 'draft';

  const status =
    contract.status || 'draft';

  if (status === 'archived') {
    return 'archived';
  }

  const today =
    new Date();

  today.setHours(0, 0, 0, 0);

  const expiration =
    contract.expirationDate
      ? new Date(
          String(contract.expirationDate)
            .slice(0, 10) +
          'T00:00:00'
        )
      : null;

  if (
    expiration &&
    expiration < today &&
    [
      'signed',
      'active',
      'renewal_due'
    ].includes(status)
  ) {
    return 'expired';
  }

  const effective =
    contract.effectiveDate
      ? new Date(
          String(contract.effectiveDate)
            .slice(0, 10) +
          'T00:00:00'
        )
      : null;

  if (
    status === 'signed' &&
    effective &&
    effective <= today
  ) {
    return 'active';
  }

  return status;
}

formatContractStatus(status) {
  const labels = {
    draft: 'Draft',
    sent: 'Sent for Signature',
    viewed: 'Viewed',
    pending_signature: 'Awaiting Signature',
    signed: 'Signed',
    active: 'Active',
    renewal_due: 'Renewal Due',
    expired: 'Expired',
    archived: 'Archived'
  };

  return labels[status] || 'Agreement';
}

formatDate(value) {
  if (!value) return '';

  const raw = String(value);

  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [year, month, day] =
      raw.split('-').map(Number);

    return new Date(
      year,
      month - 1,
      day
    ).toLocaleDateString();
  }

  return new Date(value).toLocaleDateString();
}

  async loadPreviousPlaybooks() {
    const container =
      this.root.querySelector(
        '[data-aa-previous-playbooks]'
      );

    if (
      !container ||
      !window.AAPlaybookService
    ) {
      return;
    }

    const workspace =
      window.AAWorkspaceContext.get();

    this.previousPlaybooks =
      await window.AAPlaybookService
        .getPlaybooksByClient({
          clientId: workspace.clientId
        });

    this.pastPlaybookVisibleCount = 6;

    this.renderPreviousPlaybooksManager(
      container
    );
  }

  renderPreviousPlaybooksManager(container) {
    const playbooks =
      [...(this.previousPlaybooks || [])];

    if (!playbooks.length) {
      container.innerHTML = `
        <div class="aa-playbook-library-empty">
          <strong>No Playbooks yet.</strong>
          <p>Your Playbook history will appear here.</p>
        </div>
      `;

      return;
    }

    const current =
      window.AAPlaybookService
        .getCurrentProductionMonth?.() ||
      null;

    const currentValue =
      current
        ? Number(
            `${current.year}${String(
              current.month
            ).padStart(2, '0')}`
          )
        : null;

    const sorted =
      playbooks.sort(
        (a, b) =>
          this.getPlaybookDateValue(a) -
          this.getPlaybookDateValue(b)
      );

    const currentPlaybook =
      currentValue === null
        ? null
        : sorted.find(
            (playbook) =>
              this.getPlaybookDateValue(
                playbook
              ) === currentValue
          ) || null;

    const upcoming =
      sorted.filter(
        (playbook) =>
          currentValue !== null &&
          this.getPlaybookDateValue(
            playbook
          ) > currentValue
      );

    const past =
      sorted
        .filter(
          (playbook) =>
            currentValue === null ||
            this.getPlaybookDateValue(
              playbook
            ) < currentValue
        )
        .sort(
          (a, b) =>
            this.getPlaybookDateValue(b) -
            this.getPlaybookDateValue(a)
        );

    const sections = [];

    if (currentPlaybook) {
      sections.push(
        this.renderPlaybookLibrarySection({
          title: 'Current',
          count: 1,
          className:
            'aa-playbook-library-section--current',
          content:
            this.renderPlaybookLibraryCard(
              currentPlaybook,
              'current'
            )
        })
      );
    }

    if (upcoming.length) {
      sections.push(
        this.renderPlaybookLibrarySection({
          title: 'Upcoming',
          count: upcoming.length,
          content: `
            <div class="aa-playbook-library-grid">
              ${upcoming
                .map((playbook) =>
                  this.renderPlaybookLibraryCard(
                    playbook,
                    'upcoming'
                  )
                )
                .join('')}
            </div>
          `
        })
      );
    }

    if (past.length) {
      const visiblePast =
        past.slice(
          0,
          this.pastPlaybookVisibleCount
        );

      const remainingPast =
        Math.max(
          past.length -
          visiblePast.length,
          0
        );

      sections.push(
        this.renderPlaybookLibrarySection({
          title: 'Past Playbooks',
          count: past.length,
          content: `
            ${this.renderPastPlaybookYears(
              visiblePast
            )}

            ${remainingPast > 0 ? `
              <div class="aa-playbook-library-more">
                <button
                  type="button"
                  class="aa-button aa-button--secondary aa-button--small"
                  data-aa-load-more-playbooks
                >
                  Load 6 More
                </button>

                <span>
                  ${remainingPast} older Playbook${remainingPast === 1 ? '' : 's'}
                </span>
              </div>
            ` : ''}
          `
        })
      );
    }

    container.innerHTML =
      sections.join('');
  }

  renderPlaybookLibrarySection({
    title,
    count,
    className = '',
    content
  }) {
    return `
      <section class="aa-playbook-library-section ${className}">
        <header class="aa-playbook-library-section__header">
          <h2>${this.escapeCatalogHtml(title)}</h2>
          <span>${Number(count || 0)}</span>
        </header>
        ${content}
      </section>
    `;
  }

  renderPlaybookLibraryCard(
    playbook,
    context = 'past'
  ) {
    const title =
      `${this.getMonthName(
        playbook.month
      )} ${playbook.year}`;

    const primaryAction =
      window.AAPlaybookService
        .getPlaybookPrimaryAction(
          playbook
        );

    const status =
      primaryAction.status;

    const statusLabel =
      window.AAPlaybookService
        .formatPlaybookStatus(
          status
        );

    const actionLabel =
      context === 'current'
        ? (
            primaryAction.editable
              ? 'Continue'
              : 'View'
          )
        : context === 'upcoming'
          ? 'Plan'
          : (
              primaryAction.editable
                ? 'Continue'
                : 'View'
            );

    return `
      <article
        class="aa-playbook-library-card aa-playbook-library-card--${context}"
      >
        <div class="aa-playbook-library-card__top">
          <span
            class="aa-status-pill"
            data-status="${this.escapeCatalogHtml(status)}"
          >
            ${this.escapeCatalogHtml(statusLabel)}
          </span>

          <span class="aa-playbook-library-card__updated">
            ${this.escapeCatalogHtml(
              this.formatPlaybookUpdatedAt(
                playbook.updated_at
              )
            )}
          </span>
        </div>

        <div class="aa-playbook-library-card__body">
          <h3>${this.escapeCatalogHtml(title)}</h3>

          <a
            class="aa-button aa-button--primary aa-button--small"
            href="${this.escapeCatalogHtml(primaryAction.href)}"
          >
            ${this.escapeCatalogHtml(actionLabel)}
          </a>
        </div>
      </article>
    `;
  }

  renderPastPlaybookYears(
    playbooks
  ) {
    const byYear =
      playbooks.reduce(
        (groups, playbook) => {
          const year =
            String(playbook.year || '');

          if (!groups[year]) {
            groups[year] = [];
          }

          groups[year].push(
            playbook
          );

          return groups;
        },
        {}
      );

    const years =
      Object.keys(byYear)
        .sort(
          (a, b) =>
            Number(b) - Number(a)
        );

    return years
      .map(
        (year, index) => {
          const cards = `
            <div class="aa-playbook-library-grid">
              ${byYear[year]
                .map((playbook) =>
                  this.renderPlaybookLibraryCard(
                    playbook,
                    'past'
                  )
                )
                .join('')}
            </div>
          `;

          if (index === 0) {
            return `
              <div class="aa-playbook-library-year">
                <div class="aa-playbook-library-year__label">
                  ${this.escapeCatalogHtml(year)}
                </div>
                ${cards}
              </div>
            `;
          }

          return `
            <details class="aa-playbook-library-year aa-playbook-library-year--collapsed">
              <summary>
                <span>
                  View ${this.escapeCatalogHtml(year)} Playbooks
                </span>
                <small>
                  ${byYear[year].length}
                </small>
              </summary>

              <div class="aa-playbook-library-year__content">
                ${cards}
              </div>
            </details>
          `;
        }
      )
      .join('');
  }

  formatPlaybookUpdatedAt(value) {
    if (!value) {
      return 'Not updated yet';
    }

    const date =
      new Date(value);

    if (
      Number.isNaN(
        date.getTime()
      )
    ) {
      return 'Updated';
    }

    return `Updated ${date.toLocaleDateString(
      'en-US',
      {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      }
    )}`;
  }

  getPlaybookDateValue(playbook) {
    return Number(
      `${playbook.year || 0}${String(
        playbook.month || 0
      ).padStart(2, '0')}`
    );
  }

  getMonthName(month) {
    const monthNames = [
      '',
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December'
    ];

    return monthNames[month] || month;
  }

  bindEvents() {
    this.root.addEventListener('click', (event) => {
      const loadMore =
        event.target.closest(
          '[data-aa-load-more-playbooks]'
        );

      if (loadMore) {
        this.pastPlaybookVisibleCount += 6;

        const container =
          this.root.querySelector(
            '[data-aa-previous-playbooks]'
          );

        if (container) {
          this.renderPreviousPlaybooksManager(
            container
          );
        }

        return;
      }

      const link = event.target.closest(
        'a[href^="/pages/workspace-"]'
      );

      if (!link) return;

      const workspace =
        window.AAWorkspaceContext?.get?.();

      const adminClientId =
        workspace?.isInternal
          ? window.AAAdminWorkspaceView?.getClientId?.()
          : null;

      if (!adminClientId) return;

      const url = new URL(
        link.href,
        window.location.origin
      );

      url.searchParams.set(
        'aa_admin_client',
        adminClientId
      );

      link.href =
        `${url.pathname}${url.search}${url.hash}`;
    });

    if (this.toggle) {
      this.toggle.addEventListener('click', () => {
        if (this.root.classList.contains('is-sidebar-open')) this.closeSidebar();
        else this.openSidebar();
      });
    }

    this.root.querySelector('[data-aa-menu-close]')
      ?.addEventListener('click', () => this.closeSidebar());

    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.closeSidebar());
    }

    const signOutButton =
      this.root.querySelector('[data-aa-workspace-sign-out]');

    if (signOutButton) {
      signOutButton.addEventListener('click', async () => {
        signOutButton.disabled = true;

        try {
          await window.AAWorkspaceContext?.signOut?.();
        } catch (error) {
          console.error('Unable to sign out.', error);
          signOutButton.disabled = false;
        }
      });
    }

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.closeSidebar();
    });
  }

  openSidebar() {
    this.root.classList.add('is-sidebar-open');
  }

  closeSidebar() {
    this.root.classList.remove('is-sidebar-open');
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-aa-workspace]').forEach((workspace) => {
    new AAWorkspaceShell(workspace);
  });
});

class AAPlaybook {
  constructor(root) {
    this.root = root;
    this.cards = root.querySelectorAll('.aa-section-card');

    this.bindEvents();
  }

  bindEvents() {
    this.cards.forEach((card) => {
      card.addEventListener('click', (event) => {
        const link = event.target.closest('a');

        if (!link) {
          const cardLink = card.querySelector('a');
          if (cardLink) cardLink.click();
        }
      });
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.aa-playbook').forEach((playbook) => {
    new AAPlaybook(playbook);
  });
});
